/**
 * Wear Felicity self-hosted support chat widget.
 *
 * Vanilla JS, no jQuery. Single IIFE. Talks ONLY to the backend configured via
 * #wf-chat-root[data-backend]. Does NOT patch window.fetch/XHR (theme.liquid
 * already does that for cart updates — see layout/theme.liquid). All server
 * strings are rendered via textContent / DOM nodes, never innerHTML.
 *
 * Accessibility: dialog semantics + focus trap + Escape + focus restore on the
 * panel; every control is a real <button>/<input>/<textarea>; icon-only buttons
 * carry visually-hidden text; async updates announce via aria-live regions;
 * links to new windows get rel=noopener + hidden "(opens in a new window)".
 *
 * UI matches John's Claude Design mockup (hero home, bottom tab bar, timeline
 * order tracking). The WS/state/token/a11y plumbing is unchanged from the
 * reviewed implementation — only the presentation layer was reskinned.
 */
(function () {
  'use strict';

  var root = document.getElementById('wf-chat-root');
  if (!root) return;

  var BACKEND = (root.getAttribute('data-backend') || '').replace(/\/+$/, '');
  if (!BACKEND) return;

  var LOGO_URL = root.getAttribute('data-logo') || '';
  var CUSTOMER_EMAIL = root.getAttribute('data-customer-email') || '';
  var CUSTOMER_NAME = root.getAttribute('data-customer-name') || '';

  /*==========================================================================
    Small helpers
  ==========================================================================*/
  var SVG_NS = 'http://www.w3.org/2000/svg';

  // localStorage guarded for Safari private mode / disabled storage.
  var store = {
    get: function (k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
    set: function (k, v) { try { window.localStorage.setItem(k, v); } catch (e) {} },
    del: function (k) { try { window.localStorage.removeItem(k); } catch (e) {} }
  };

  // sessionStorage-backed helper for the order-verify session (survives view
  // switches but not a new browser session). Guarded the same way.
  var sess = {
    get: function (k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } },
    set: function (k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) {} },
    del: function (k) { try { window.sessionStorage.removeItem(k); } catch (e) {} }
  };

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  // Build an inline SVG from an array of path "d" strings. `opts` may set
  // strokeWidth (default 1.8), size (viewBox stays 24), and fill (default none).
  function svg(paths, cls, opts) {
    opts = opts || {};
    var s = document.createElementNS(SVG_NS, 'svg');
    s.setAttribute('viewBox', '0 0 24 24');
    s.setAttribute('fill', opts.fill || 'none');
    s.setAttribute('aria-hidden', 'true');
    if (cls) s.setAttribute('class', cls);
    paths.forEach(function (d) {
      var p = document.createElementNS(SVG_NS, 'path');
      p.setAttribute('d', d);
      p.setAttribute('stroke', 'currentColor');
      p.setAttribute('stroke-width', String(opts.strokeWidth || 1.8));
      p.setAttribute('stroke-linecap', 'round');
      p.setAttribute('stroke-linejoin', 'round');
      s.appendChild(p);
    });
    return s;
  }

  function srSpan(text) {
    return el('span', 'wf-chat-sr', text);
  }

  // Append a control's visible + screen-reader text safely.
  function iconButton(cls, srText, svgNode, ariaLabel) {
    var b = el('button', cls);
    b.type = 'button';
    if (ariaLabel) b.setAttribute('aria-label', ariaLabel);
    if (svgNode) b.appendChild(svgNode);
    if (srText) b.appendChild(srSpan(srText));
    return b;
  }

  // Split plain-text answer into paragraph/br DOM nodes (never innerHTML).
  function renderPlainText(container, text) {
    var paras = String(text == null ? '' : text).split(/\n\n+/);
    paras.forEach(function (para) {
      var p = el('p');
      var lines = para.split('\n');
      lines.forEach(function (line, i) {
        if (i > 0) p.appendChild(document.createElement('br'));
        p.appendChild(document.createTextNode(line));
      });
      container.appendChild(p);
    });
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function fmtDate(iso) {
    try {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) { return ''; }
  }

  // Shopify CDN images accept width params for cheap thumbnails.
  function thumbUrl(src, w) {
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  // "$53.60" for USD (the store default); "53.60 CAD" otherwise.
  function fmtMoney(price, currency) {
    if (price == null || price === '') return '';
    if (!currency || currency === 'USD') return '$' + price;
    return price + ' ' + currency;
  }

  // Loose email test — good enough to decide email-vs-phone for the verify flow.
  function isEmail(s) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim()); }

  // Relative time for the conversations card: "5m ago" / "2h ago" / "Aug 6".
  function relTime(iso) {
    try {
      var then = new Date(iso).getTime();
      if (isNaN(then)) return '';
      var diff = Date.now() - then;
      if (diff < 0) diff = 0;
      var mins = Math.floor(diff / 60000);
      if (mins < 1) return 'just now';
      if (mins < 60) return mins + 'm ago';
      var hrs = Math.floor(mins / 60);
      if (hrs < 24) return hrs + 'h ago';
      return fmtDate(iso);
    } catch (e) { return ''; }
  }

  function fmtTime(iso) {
    try {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    } catch (e) { return ''; }
  }

  function api(path) { return BACKEND + path; }

  function getJSON(path, signal) {
    return fetch(api(path), { method: 'GET', headers: { 'Accept': 'application/json' }, signal: signal })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, status: r.status, body: j }; }); });
  }

  function postJSON(path, data) {
    return fetch(api(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(data)
    }).then(function (r) { return r.json().then(function (j) { return { ok: r.ok, status: r.status, body: j }; }); });
  }

  /*==========================================================================
    Skeleton placeholders — Richpanel-style shimmer bars shown while async
    content loads. Shimmer nodes are decorative: every group carries
    aria-hidden and sits next to a visually-hidden label so screen readers
    still hear a pending state. Reduced-motion kills the sweep in CSS.
  ==========================================================================*/
  function skelBar(cls, width) {
    var b = el('span', 'wf-chat-skel' + (cls ? ' ' + cls : ''));
    if (width) b.style.width = width;
    return b;
  }

  // SR-only label + aria-hidden shimmer container, appended to `parent`.
  // Returns the container for the caller to fill with bars.
  function skelGroup(parent, label, cls) {
    parent.appendChild(srSpan(label));
    var g = el('div', cls);
    g.setAttribute('aria-hidden', 'true');
    parent.appendChild(g);
    return g;
  }

  // --- Shared icon path sets (stroke SVGs) ---
  var ICON = {
    close: ['M18 6 6 18', 'M6 6l12 12'],
    back: ['M15 18l-6-6 6-6'],
    chevronRight: ['M9 6l6 6-6 6'],
    chevronDown: ['M6 9l6 6 6-6'],
    search: ['M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14z', 'm21 21-4.3-4.3'],
    chat: ['M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z'],
    home: ['m3 10 9-7 9 7v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1Z'],
    help: ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z', 'M9.2 9a2.9 2.9 0 0 1 5.6 1c0 1.8-2.8 2.2-2.8 3.6', 'M12 17h.01'],
    box: ['M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z', 'm3.3 7 8.7 5 8.7-5', 'M12 22V12'],
    camera: ['M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z', 'M12 10a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
    truck: ['M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2', 'M15 18H9', 'M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.62l-3.48-4.35A1 1 0 0 0 17.52 8H14', 'M17 16a2 2 0 1 0 0 4 2 2 0 0 0 0-4z', 'M7 16a2 2 0 1 0 0 4 2 2 0 0 0 0-4z'],
    returns: ['M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8', 'M3 3v5h5'],
    send: ['M22 2 11 13', 'M22 2 15 22l-4-9-9-4Z'],
    check: ['M20 6 9 17l-5-5'],
    star: ['M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z'],
    envelope: ['M3 6a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1Z', 'm3 7 9 6 9-6'],
    logout: ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'm16 17 5-5-5-5', 'M21 12H9']
  };

  /*==========================================================================
    State
  ==========================================================================*/
  var state = {
    config: null,
    open: false,
    view: 'home',
    lastFocus: null,        // trigger to restore focus to on close
    orderRef: null,         // carried into chat when arriving from order lookup
    seenMsgIds: {},         // dedupe restored + pushed messages by id
    csatDone: false,
    sessionRebuilt: false,  // true once a WS 'session' frame rebuilt the thread
    hasCustomerMsg: false   // hide quick-reply chips once the user has spoken
  };

  var ws = null;
  var wsReconnectAttempts = 0;
  var wsReconnectTimer = null;
  var pingTimer = null;
  var typingSentAt = 0;
  var outbox = [];          // messages typed while the socket wasn't OPEN
  var outboxNoticeTimer = null;
  var opening = false;      // true only during open(); suppresses setView focus-move

  /*==========================================================================
    Boot: fetch config ~2s after DOMContentLoaded (or on idle). If disabled or
    the request fails, render nothing.
  ==========================================================================*/
  function boot() {
    getJSON('/support/widget/config/').then(function (res) {
      if (!res.ok || !res.body || res.body.enabled === false) return;
      state.config = res.body;
      try {
        build();
      } catch (e) {
        // Never leave a half-built widget in the DOM.
        try { root.textContent = ''; } catch (e2) {}
      }
    }).catch(function () { /* silent: render nothing */ });
  }

  function scheduleBoot() {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(function () { boot(); }, { timeout: 3000 });
    } else {
      setTimeout(boot, 2000);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(scheduleBoot, 2000); });
  } else {
    setTimeout(scheduleBoot, 2000);
  }

  /*==========================================================================
    DOM references (populated in build())
  ==========================================================================*/
  var launcher, panel, badge, liveRegion, extLiveRegion, bodyEl;
  var header, headerBrand, headerStatus, backBtn;
  var heroLogoImg = null;   // hero logo <img>; src assigned on first open()
  var views = {};
  var tabButtons = {};      // { home, chat, help }
  var homeEls = {};         // home-view async slots: convoSlot, logoutSlot
  var ONLINE = true;

  /*==========================================================================
    Build the widget
  ==========================================================================*/
  function build() {
    ONLINE = state.config.online !== false;

    // ---- Launcher (56px circle, chat bubble / chevron-down) --------------
    launcher = el('button', 'wf-chat-launcher');
    launcher.type = 'button';
    launcher.setAttribute('aria-haspopup', 'dialog');
    launcher.setAttribute('aria-expanded', 'false');
    launcher.setAttribute('aria-controls', 'wf-chat-panel');
    launcher.appendChild(svg(ICON.chat, 'wf-chat-icon--open', { strokeWidth: 1.6 }));
    launcher.appendChild(svg(ICON.chevronDown, 'wf-chat-icon--close', { strokeWidth: 1.7 }));
    badge = el('span', 'wf-chat-launcher__badge');
    badge.setAttribute('aria-hidden', 'true');
    launcher.appendChild(badge);
    launcher.appendChild(srSpan('Open support chat'));
    launcher.addEventListener('click', toggle);

    // ---- Panel -----------------------------------------------------------
    panel = el('div', 'wf-chat-panel');
    panel.id = 'wf-chat-panel';
    panel.setAttribute('role', 'dialog');
    // tabindex -1 so we can move focus to the dialog itself on open (SRs enter
    // the dialog; keyboard users then Tab to controls) without lighting up a
    // control's :focus-visible ring on a pointer open.
    panel.setAttribute('tabindex', '-1');
    // No aria-modal: on desktop the page behind stays scrollable/clickable, so
    // claiming modality would wrongly hide the rest of the page from SR users.
    // A non-modal dialog with a (gated) focus trap is the honest description.
    panel.setAttribute('aria-label', 'Wear Felicity support chat');

    // ---- Chrome header (non-home views only) -----------------------------
    header = el('div', 'wf-chat-header');
    backBtn = iconButton('wf-chat-header__btn wf-chat-header__back', 'Back',
      svg(ICON.back, null, { strokeWidth: 1.8 }), 'Back');
    backBtn.addEventListener('click', goBack);
    var brandBlock = el('div', 'wf-chat-header__brand');
    headerBrand = el('h2', 'wf-chat-header__wordmark', 'Wear Felicity');
    headerStatus = el('p', 'wf-chat-header__status' + (ONLINE ? '' : ' wf-chat-header__status--away'),
      ONLINE ? 'Online · replies in minutes' : 'Away — we reply by email');
    brandBlock.appendChild(headerBrand);
    brandBlock.appendChild(headerStatus);
    var closeBtn = iconButton('wf-chat-header__btn wf-chat-header__close', 'Close support chat',
      svg(ICON.close, null, { strokeWidth: 1.8 }), 'Close support chat');
    closeBtn.addEventListener('click', close);
    header.appendChild(backBtn);
    header.appendChild(brandBlock);
    header.appendChild(closeBtn);
    panel.appendChild(header);

    // ---- Body (holds all views) ------------------------------------------
    bodyEl = el('div', 'wf-chat-body');
    panel.appendChild(bodyEl);

    // In-panel aria-live announcer (polite) for async status.
    liveRegion = el('div', 'wf-chat-sr');
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', 'polite');
    panel.appendChild(liveRegion);

    buildHomeView();
    buildHelpView();
    buildOrderView();
    buildChatView();
    buildOfflineView();

    // ---- Bottom tab bar (persistent) -------------------------------------
    panel.appendChild(buildTabBar());

    setView('home');

    // Persistent announcer that lives OUTSIDE the panel (which is display:none
    // when closed). SR users must still hear "new message" while the panel is
    // closed, so closed-state announcements route through this region.
    extLiveRegion = el('div', 'wf-chat-sr');
    extLiveRegion.setAttribute('role', 'status');
    extLiveRegion.setAttribute('aria-live', 'polite');

    root.appendChild(launcher);
    root.appendChild(extLiveRegion);
    root.appendChild(panel);

    document.addEventListener('keydown', onGlobalKeydown, true);

    // Keyboard-vs-pointer modality flag on the root: a pointer press clears it
    // (mouse clicks into fields show no focus bar); a Tab/arrow keydown sets it
    // (keyboard users keep a visible focus indicator). Gated CSS reads
    // .wf-chat-kbd. Capture phase so we see the event before anything else.
    document.addEventListener('pointerdown', function () {
      root.classList.remove('wf-chat-kbd');
    }, true);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Tab' || (e.key && e.key.indexOf('Arrow') === 0)) {
        root.classList.add('wf-chat-kbd');
      }
    }, true);
  }

  function announceVia(region, msg) {
    if (!region) return;
    region.textContent = '';
    // Reassign on next tick so repeated identical messages re-announce.
    setTimeout(function () { region.textContent = msg; }, 30);
  }

  // In-panel announcer (search counts, order lookup, CSAT) — only heard while open.
  function announce(msg) { announceVia(liveRegion, msg); }

  // Always-audible announcer (new-message alerts while the panel is closed).
  function announceExternal(msg) { announceVia(extLiveRegion, msg); }

  function ctaLabel() { return ONLINE ? 'Chat with us' : 'Leave a message'; }
  function ctaView() { return ONLINE ? 'chat' : 'offline'; }

  /*==========================================================================
    Bottom tab bar
  ==========================================================================*/
  function buildTabBar() {
    var bar = el('nav', 'wf-chat-tabs');
    bar.setAttribute('aria-label', 'Support sections');
    tabButtons.home = tabButton('Home', ICON.home, function () { setView('home'); });
    tabButtons.chat = tabButton('Chat', ICON.chat, function () { setView(ctaView()); });
    tabButtons.help = tabButton('Help', ICON.help, function () { setView('help'); });
    bar.appendChild(tabButtons.home);
    bar.appendChild(tabButtons.chat);
    bar.appendChild(tabButtons.help);
    return bar;
  }

  function tabButton(label, iconPaths, onClick) {
    var b = el('button', 'wf-chat-tab');
    b.type = 'button';
    b.appendChild(svg(iconPaths, 'wf-chat-tab__icon', { strokeWidth: 1.7 }));
    b.appendChild(el('span', 'wf-chat-tab__label', label));
    b.addEventListener('click', onClick);
    return b;
  }

  // Reflect the active section on the tab bar. Chat tab is active on both the
  // chat and offline/contact views; order view highlights no tab.
  function updateTabs(name) {
    var activeTab = name === 'home' ? 'home'
      : (name === 'chat' || name === 'offline') ? 'chat'
      : name === 'help' ? 'help'
      : null;
    ['home', 'chat', 'help'].forEach(function (k) {
      var on = k === activeTab;
      tabButtons[k].classList.toggle('wf-chat-tab--active', on);
      if (on) tabButtons[k].setAttribute('aria-current', 'true');
      else tabButtons[k].removeAttribute('aria-current');
    });
  }

  /*==========================================================================
    Views
  ==========================================================================*/
  function makeView(name, active) {
    var v = el('div', 'wf-chat-view wf-chat-view--' + name + (active ? ' wf-chat-view--active' : ''));
    views[name] = v;
    bodyEl.appendChild(v);
    return v;
  }

  /*==========================================================================
    HOME view — self-serve first. An open ivory top (logo, heading, big
    search + popular-search chips), a featured "Where's my order?" card, then
    one Quick links list where messaging is just one row — the only place
    the online status shows. Figma: "Support Chat Widget — Redesign" >
    HOME — Option F (Oct 2026).
  ==========================================================================*/
  // "● We typically reply in a few minutes" — a span (not p) so it can sit
  // inside a button. The dot is decorative; the words carry the state.
  function presenceLine(cls) {
    var p = el('span', 'wf-chat-presence' + (ONLINE ? '' : ' wf-chat-presence--away') + (cls ? ' ' + cls : ''));
    var dot = el('span', 'wf-chat-presence__dot');
    dot.setAttribute('aria-hidden', 'true');
    p.appendChild(dot);
    p.appendChild(document.createTextNode(ONLINE
      ? 'We typically reply in a few minutes'
      : 'Away now — we’ll reply by email'));
    return p;
  }

  // Decorative "WF" monogram for the team (the name always sits beside it).
  function avatarBadge() {
    var a = el('span', 'wf-chat-avatar', 'WF');
    a.setAttribute('aria-hidden', 'true');
    return a;
  }

  // Popular-search chips: specific questions that aren't already a quick
  // link. `q` is what lands in the search box and goes to the FAQ search.
  var HOME_CHIPS = [
    { label: 'Arrive by a date?', q: 'arrive by' },
    { label: 'Cancel order', q: 'cancel' },
    { label: 'Jewelry care', q: 'jewelry care' }
  ];

  function buildHomeView() {
    var v = makeView('home', true);

    // ---- Top: logo + close, heading, search, chips ----
    var top = el('div', 'wf-chat-top');
    var topBar = el('div', 'wf-chat-top__bar');
    // Script-logo image when the snippet provides one (a white PNG on the
    // theme CDN, inked by CSS); the h2 stays for heading structure and the
    // img alt carries the accessible name. Text wordmark is the fallback.
    var brand = el('h2', 'wf-chat-top__wordmark');
    if (LOGO_URL) {
      heroLogoImg = document.createElement('img');
      heroLogoImg.className = 'wf-chat-top__logo';
      // src waits for the first open() — a set src downloads immediately,
      // even inside the hidden panel, costing every page view the PNG.
      heroLogoImg.alt = 'Wear Felicity';
      heroLogoImg.addEventListener('error', function () {
        brand.textContent = 'Wear Felicity';
      });
      brand.appendChild(heroLogoImg);
    } else {
      brand.textContent = 'Wear Felicity';
    }
    topBar.appendChild(brand);
    var topClose = iconButton('wf-chat-top__close', 'Close support chat',
      svg(ICON.close, null, { strokeWidth: 1.8 }), 'Close support chat');
    topClose.addEventListener('click', close);
    topBar.appendChild(topClose);
    top.appendChild(topBar);

    top.appendChild(el('p', 'wf-chat-top__title',
      CUSTOMER_NAME ? 'Hi ' + CUSTOMER_NAME + ', how can we help?' : 'How can we help?'));
    top.appendChild(el('p', 'wf-chat-top__sub', 'Search, tap a topic, or message us.'));

    var search = el('div', 'wf-chat-search wf-chat-search--hero');
    search.appendChild(svg(ICON.search, 'wf-chat-search__icon', { strokeWidth: 1.8 }));
    var searchLabel = el('label', 'wf-chat-sr', 'Search help topics');
    searchLabel.setAttribute('for', 'wf-chat-search-input');
    var searchInput = el('input', 'wf-chat-search__input');
    searchInput.type = 'search';
    searchInput.id = 'wf-chat-search-input';
    searchInput.placeholder = 'Search shipping, engraving, returns…';
    searchInput.autocomplete = 'off';
    search.appendChild(searchLabel);
    search.appendChild(searchInput);
    top.appendChild(search);

    var chips = el('div', 'wf-chat-chips');
    chips.setAttribute('role', 'group');
    chips.setAttribute('aria-label', 'Popular searches');
    HOME_CHIPS.forEach(function (c) {
      var b = el('button', 'wf-chat-chip', c.label);
      b.type = 'button';
      b.addEventListener('click', function () {
        searchInput.value = c.q;
        searchNow();
      });
      chips.appendChild(b);
    });
    top.appendChild(chips);
    v.appendChild(top);

    // ---- Search results (only while a query is active) ----
    var results = el('div', 'wf-chat-card wf-chat-results');
    results.hidden = true;
    results.appendChild(el('h3', 'wf-chat-results__label', 'Results'));
    var resultsList = el('ul', 'wf-chat-rowlist');
    resultsList.setAttribute('aria-label', 'Search results');
    results.appendChild(resultsList);
    v.appendChild(results);

    var searchSeq = 0;   // drops responses that arrive after a newer query
    function searchNow() {
      var q = searchInput.value.trim();
      var seq = ++searchSeq;
      if (q.length < 2) {
        results.hidden = true;
        resultsList.textContent = '';
        return;
      }
      getJSON('/support/widget/faqs/?q=' + encodeURIComponent(q)).then(function (res) {
        if (seq !== searchSeq) return;
        results.hidden = false;
        resultsList.textContent = '';
        var faqs = (res.body && res.body.faqs) || [];
        if (!faqs.length) {
          var li = el('li');
          li.appendChild(el('p', 'wf-chat-results-card__empty',
            'No matches — try "shipping", "engraving", or "returns".'));
          resultsList.appendChild(li);
          announce('No FAQ results');
          return;
        }
        faqs.forEach(function (f) { resultsList.appendChild(wrapLi(faqResultRow(f))); });
        announce(faqs.length + (faqs.length === 1 ? ' result' : ' results'));
      }).catch(function () {});
    }
    searchInput.addEventListener('input', debounce(searchNow, 300));

    // ---- Conversations card slot (returning visitors; populated async) ----
    var convoSlot = el('div', 'wf-chat-convos-slot');
    homeEls.convoSlot = convoSlot;
    v.appendChild(convoSlot);

    // ---- Featured order card: the #1 reason people open support ----
    var orderCard = el('button', 'wf-chat-ordercard');
    orderCard.type = 'button';
    var oTile = el('span', 'wf-chat-ordercard__tile');
    oTile.appendChild(svg(ICON.box, null, { strokeWidth: 1.8 }));
    orderCard.appendChild(oTile);
    var oText = el('span', 'wf-chat-ordercard__text');
    oText.appendChild(el('span', 'wf-chat-ordercard__title', 'Where’s my order?'));
    oText.appendChild(el('span', 'wf-chat-ordercard__sub', 'Track, change address or cancel'));
    orderCard.appendChild(oText);
    var oArrow = el('span', 'wf-chat-ordercard__arrow');
    oArrow.appendChild(svg(ICON.chevronRight, null, { strokeWidth: 2 }));
    orderCard.appendChild(oArrow);
    orderCard.addEventListener('click', function () { setView('order'); });
    v.appendChild(orderCard);

    // ---- Quick links. Category names must match the backend SupportFAQ
    // categories; a renamed category just falls back to the full Help list.
    v.appendChild(el('h3', 'wf-chat-home-label', 'Quick links'));
    var qlCard = el('div', 'wf-chat-card wf-chat-quicklinks');
    var qlList = el('ul', 'wf-chat-rowlist');
    [
      { label: 'Photos & engraving', icon: ICON.camera,
        go: function () { setView('help', { category: 'Photos & Personalization' }); } },
      { label: 'Production & shipping times', icon: ICON.truck,
        go: function () { setView('help', { category: 'Production & Shipping' }); } },
      { label: 'Returns & exchanges', icon: ICON.returns,
        go: function () { setView('help', { category: 'Returns & Exchanges' }); } },
      // Always starts a NEW conversation — earlier ones stay reachable
      // through the "Your Conversations" card.
      { label: ONLINE ? 'Send us a message' : 'Leave us a message', icon: ICON.chat, presence: true,
        go: function () { startNewConversation(); setView(ctaView()); } },
      { label: 'Browse all help topics', icon: ICON.help,
        go: function () { setView('help'); } }
    ].forEach(function (it) { qlList.appendChild(wrapLi(quickLink(it))); });
    qlCard.appendChild(qlList);
    v.appendChild(qlCard);

    // ---- Logout slot (only populated when a verify session exists) ----
    var logoutSlot = el('div', 'wf-chat-logout-slot');
    homeEls.logoutSlot = logoutSlot;
    v.appendChild(logoutSlot);
  }

  function quickLink(it) {
    var b = el('button', 'wf-chat-quicklink');
    b.type = 'button';
    var tile = el('span', 'wf-chat-quicklink__tile');
    tile.appendChild(svg(it.icon, null, { strokeWidth: 1.8 }));
    b.appendChild(tile);
    var text = el('span', 'wf-chat-quicklink__text');
    text.appendChild(el('span', 'wf-chat-quicklink__label', it.label));
    if (it.presence) text.appendChild(presenceLine());
    b.appendChild(text);
    var chev = el('span', 'wf-chat-quicklink__chevron');
    chev.appendChild(svg(ICON.chevronRight, null, { strokeWidth: 1.8 }));
    b.appendChild(chev);
    b.addEventListener('click', it.go);
    return b;
  }

  // FAQ list, fetched at most once per page and shared by the Help view.
  // Never requested on page load — only once Help opens — so passive
  // visitors pay nothing for it.
  var faqsPromise = null;
  function loadFaqs() {
    if (!faqsPromise) {
      faqsPromise = getJSON('/support/widget/faqs/').then(function (res) {
        if (!res.ok) throw new Error('faqs ' + res.status);
        return (res.body && res.body.faqs) || [];
      });
      faqsPromise.catch(function () { faqsPromise = null; });   // allow a retry
    }
    return faqsPromise;
  }

  /*==========================================================================
    HOME extras: "Your Conversations" card + Logout (refreshed on home entry).
  ==========================================================================*/
  function refreshHomeExtras() {
    renderLogoutSlot();
    refreshConversations();
  }

  function renderLogoutSlot() {
    var slot = homeEls.logoutSlot;
    if (!slot) return;
    slot.textContent = '';
    if (!sess.get('wf_chat_session')) return;   // only for verified sessions
    var card = el('div', 'wf-chat-card wf-chat-logout-card');
    var btn = el('button', 'wf-chat-logout-btn');
    btn.type = 'button';
    var tile = el('span', 'wf-chat-logout-btn__icon');
    tile.appendChild(svg(ICON.logout, null, { strokeWidth: 1.8 }));
    btn.appendChild(tile);
    btn.appendChild(el('span', 'wf-chat-logout-btn__label', 'Log out'));
    btn.addEventListener('click', function () {
      sess.del('wf_chat_session');
      sess.del('wf_chat_email_verified');
      announce('Logged out.');
      refreshHomeExtras();   // logout card disappears; convos card collapses to anon
    });
    card.appendChild(btn);
    slot.appendChild(card);
  }

  // Skeleton card in the conversations slot: first paint only (repeat home
  // entries keep the previous card while the refresh happens behind it), and
  // only when stored state says a card is actually coming — otherwise nothing
  // would replace it and the shimmer would just blink out.
  function renderConvoSkeleton() {
    var slot = homeEls.convoSlot;
    if (!slot || slot.childElementCount) return;
    if (!sess.get('wf_chat_session') && !store.get('wf_chat_token') && !prevTokens().length) return;
    var card = el('div', 'wf-chat-card wf-chat-convos wf-chat-skel-card');
    var g = skelGroup(card, 'Loading your conversations…');
    g.appendChild(skelBar('wf-chat-skel--title', '52%'));
    // Two rows with varied line widths so the shimmer reads as a list.
    [['62%', '86%'], ['48%', '70%']].forEach(function (w) {
      var row = el('div', 'wf-chat-skel-row');
      row.appendChild(skelBar('wf-chat-skel--tile'));
      var lines = el('div', 'wf-chat-skel-row__body');
      lines.appendChild(skelBar(null, w[0]));
      lines.appendChild(skelBar(null, w[1]));
      row.appendChild(lines);
      g.appendChild(row);
    });
    slot.appendChild(card);
  }

  function refreshConversations() {
    var slot = homeEls.convoSlot;
    if (!slot) return;
    renderConvoSkeleton();
    var session = sess.get('wf_chat_session');
    if (session) {
      getJSON('/support/widget/conversations/?session=' + encodeURIComponent(session)).then(function (res) {
        if (res.status === 401) {
          // Session dead — clear it and fall back to the anonymous variant.
          sess.del('wf_chat_session');
          sess.del('wf_chat_email_verified');
          renderLogoutSlot();
          renderAnonConversation();
          return;
        }
        var convos = (res.body && res.body.conversations) || [];
        renderConversationsCard(convos);
      }).catch(function () { renderAnonConversation(); });
    } else {
      renderAnonConversation();
    }
  }

  // ---- Previous-conversation tokens (anonymous history) --------------------
  // When a fresh conversation is started while another is still active, the
  // token being left is archived here so that conversation stays listed under
  // "Your Conversations" for anonymous visitors — their card is built purely
  // from stored tokens (verified sessions get the list from the server).
  var PREV_TOKENS_KEY = 'wf_chat_prev_tokens';

  function prevTokens() {
    try {
      var arr = JSON.parse(store.get(PREV_TOKENS_KEY) || '[]');
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  }

  // Tokens are Django-signed "urlsafe_b64({"c":<id>}):timestamp:sig" — the
  // SAME conversation gets re-issued under a fresh signature on every WS
  // session, so identity checks must compare the embedded conversation id,
  // never the raw string (string dedup produced duplicate history rows).
  function tokenConvoId(token) {
    try {
      var b64 = String(token).split(':')[0].replace(/-/g, '+').replace(/_/g, '/');
      while (b64.length % 4) b64 += '=';
      var id = JSON.parse(atob(b64)).c;
      return id == null ? null : String(id);
    } catch (e) { return null; }
  }

  function sameConvo(a, b) {
    if (a === b) return true;
    var ia = tokenConvoId(a);
    return ia !== null && ia === tokenConvoId(b);
  }

  function forgetPrevToken(token) {
    var arr = prevTokens().filter(function (t) { return !sameConvo(t, token); });
    if (arr.length) store.set(PREV_TOKENS_KEY, JSON.stringify(arr));
    else store.del(PREV_TOKENS_KEY);
  }

  // Archive the active token (newest first, capped) and clear it.
  function archiveCurrentToken() {
    var token = store.get('wf_chat_token');
    if (token) {
      var arr = prevTokens().filter(function (t) { return !sameConvo(t, token); });
      arr.unshift(token);
      store.set(PREV_TOKENS_KEY, JSON.stringify(arr.slice(0, 10)));
    }
    store.del('wf_chat_token');
  }

  // Leave the current conversation (it stays listed under "Your Conversations")
  // and reset all live-chat state so the chat view opens a brand-new empty
  // thread; the server creates the conversation on the first message sent.
  function startNewConversation() {
    archiveCurrentToken();
    state.seenMsgIds = {};
    state.csatDone = false;
    state.orderRef = null;
    state.sessionRebuilt = false;
    state.hasCustomerMsg = false;
    chatEls.thread = null;
    chatEls.resolvedShown = false;
    outbox = [];
    closeSocket();
  }

  // Anonymous visitor: rows built from the active token plus any archived
  // previous conversations, newest activity first.
  function renderAnonConversation() {
    var slot = homeEls.convoSlot;
    if (!slot) return;
    var current = store.get('wf_chat_token');
    var tokens = current ? [current] : [];
    prevTokens().forEach(function (t) {
      var dup = tokens.some(function (u) { return sameConvo(u, t); });
      if (!dup) tokens.push(t);
    });
    // No history → no card. Clearing here (not upfront) keeps the skeleton
    // in place until the real card is ready to swap in.
    if (!tokens.length) { slot.textContent = ''; return; }
    Promise.all(tokens.map(function (token) {
      return getJSON('/support/widget/conversation/?token=' + encodeURIComponent(token)).then(function (res) {
        if (res.status === 404) {
          // Dead token — stop tracking it wherever it lives.
          if (token === current) store.del('wf_chat_token');
          forgetPrevToken(token);
          return null;
        }
        var msgs = (res.body && res.body.messages) || [];
        if (!msgs.length) return null;
        var last = msgs[msgs.length - 1];
        return {
          token: token,
          subject: 'Chat on Wear Felicity',
          status: res.body.status,
          updated_at: last.timestamp,
          preview: { content: last.content, sender_type: last.sender_type, timestamp: last.timestamp }
        };
      }).catch(function () { return null; });
    })).then(function (rows) {
      rows = rows.filter(Boolean);
      rows.sort(function (a, b) {
        return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
      });
      renderConversationsCard(rows);
    });
  }

  function renderConversationsCard(convos) {
    var slot = homeEls.convoSlot;
    if (!slot) return;
    slot.textContent = '';
    if (!convos.length) return;

    var card = el('div', 'wf-chat-card wf-chat-convos');
    card.appendChild(el('h3', 'wf-chat-card__title', 'Your Conversations'));
    var list = el('ul', 'wf-chat-rowlist');
    card.appendChild(list);

    var VISIBLE = 3;
    var shown = convos.slice(0, VISIBLE);
    shown.forEach(function (c) {
      list.appendChild(wrapLi(conversationRow(c)));
    });

    // "View all conversations (N)" expander for the remainder.
    if (convos.length > VISIBLE) {
      var moreBtn = el('button', 'wf-chat-convos__more');
      moreBtn.type = 'button';
      moreBtn.appendChild(el('span', null, 'View all conversations '));
      moreBtn.appendChild(el('span', 'wf-chat-convos__count', String(convos.length)));
      moreBtn.addEventListener('click', function () {
        convos.slice(VISIBLE).forEach(function (c) {
          list.appendChild(wrapLi(conversationRow(c)));
        });
        moreBtn.parentNode.removeChild(moreBtn);
        announce('Showing all ' + convos.length + ' conversations.');
      });
      card.appendChild(moreBtn);
    }

    slot.appendChild(card);
  }

  function wrapLi(node) { var li = el('li'); li.appendChild(node); return li; }

  function conversationRow(c) {
    var b = el('button', 'wf-chat-convo-row');
    b.type = 'button';

    var tile = el('span', 'wf-chat-convo-row__tile');
    tile.appendChild(svg(ICON.envelope, null, { strokeWidth: 1.6 }));
    b.appendChild(tile);

    var body = el('div', 'wf-chat-convo-row__body');
    body.appendChild(el('p', 'wf-chat-convo-row__title', c.subject || 'Chat on Wear Felicity'));

    // Preview line: "You: …" for the customer, "Wear Felicity: …" for agent/ai/bot.
    var pv = c.preview || {};
    var who = (pv.sender_type === 'customer') ? 'You: '
      : (pv.sender_type === 'agent' || pv.sender_type === 'ai' || pv.sender_type === 'bot') ? 'Wear Felicity: '
      : '';
    body.appendChild(el('p', 'wf-chat-convo-row__preview', who + (pv.content || '')));

    var when = relTime(c.updated_at || pv.timestamp);
    if (when) body.appendChild(el('p', 'wf-chat-convo-row__time', 'Wear Felicity · ' + when));
    b.appendChild(body);

    // Unread dot mirrors the launcher badge state.
    if (launcher && launcher.classList.contains('wf-chat-launcher--unread')) {
      var dot = el('span', 'wf-chat-convo-row__dot');
      dot.appendChild(srSpan('unread'));
      b.appendChild(dot);
    }

    b.addEventListener('click', function () {
      // Resume this conversation: adopt its token, reset thread/seen state, go
      // to chat (history loads via the normal token resume path). Any other
      // active conversation is archived first so it stays listed on home; if
      // the row IS the active conversation (possibly under a re-issued token),
      // keep the stored token — it carries the freshest signature.
      if (c.token && !sameConvo(c.token, store.get('wf_chat_token'))) {
        archiveCurrentToken();
        forgetPrevToken(c.token);
        store.set('wf_chat_token', c.token);
      }
      state.seenMsgIds = {};
      state.sessionRebuilt = false;
      chatEls.thread = null;
      chatEls.resolvedShown = false;
      closeSocket();
      setView('chat');
    });

    return b;
  }

  // A search-result row: opens that FAQ expanded in the Help view.
  function faqResultRow(faq) {
    var b = el('button', 'wf-chat-result-row');
    b.type = 'button';
    b.appendChild(el('span', 'wf-chat-result-row__q', faq.question || ''));
    var chev = el('span', 'wf-chat-result-row__chevron');
    chev.appendChild(svg(ICON.chevronRight, null, { strokeWidth: 1.8 }));
    b.appendChild(chev);
    b.addEventListener('click', function () {
      openHelpWithFaq(faq);
    });
    return b;
  }

  /*==========================================================================
    HELP view — topic list -> full article screens (Richpanel style)
  ==========================================================================*/
  var helpScreen = 'list';   // 'list' | 'article' — drives the header back arrow
  var helpCategory = null;   // home quick links narrow the list to one category

  function buildHelpView() {
    var v = makeView('help', false);
    v._loaded = false;
    v._faqs = [];
    v._render = function (openFaq) {
      if (openFaq) {
        var match = null;
        for (var i = 0; i < v._faqs.length; i++) {
          if (matchesFaq(v._faqs[i], openFaq)) { match = v._faqs[i]; break; }
        }
        var target = match || (openFaq.question && openFaq.answer ? openFaq : null);
        if (target) { renderHelpArticle(v, target); return; }
      }
      renderHelpList(v);
    };
    // opts: { faq } opens one article, { category } narrows the list. A
    // back-navigation return keeps whichever category was showing.
    v._enter = function (opts, fromBack) {
      var openFaq = (opts && opts.faq) || null;
      if (!fromBack) helpCategory = (opts && opts.category) || null;
      if (v._loaded) {
        v._render(openFaq);
        return;
      }
      v.textContent = '';
      var skel = el('div', 'wf-chat-help-skel');
      var g = skelGroup(skel, 'Loading FAQs…');
      g.appendChild(skelBar('wf-chat-skel--title', '44%'));
      for (var i = 0; i < 4; i++) g.appendChild(skelBar(null, (92 - i * 7) + '%'));
      v.appendChild(skel);
      loadFaqs().then(function (faqs) {
        v._faqs = faqs;
        v._loaded = true;
        v._render(openFaq);
      }).catch(function () {
        v.textContent = '';
        v.appendChild(el('p', 'wf-chat-loading', 'Could not load FAQs.'));
      });
    };
    // Header back arrow steps article -> list before leaving the view.
    v._back = function () {
      if (helpScreen === 'article') {
        renderHelpList(v);
        return true;
      }
      return false;
    };
  }

  function renderHelpList(v) {
    helpScreen = 'list';
    v.textContent = '';
    // Narrowed to one category (from a home quick link) only if it exists;
    // the header then names the category instead of "Help".
    var onlyCat = helpCategory && v._faqs.some(function (f) { return (f.category || '') === helpCategory; })
      ? helpCategory : null;
    headerBrand.textContent = onlyCat || VIEW_TITLES.help;
    if (!v._faqs.length) {
      v.appendChild(el('p', 'wf-chat-loading', 'No FAQs yet.'));
    } else {
      // Instant client-side filter over the already-loaded topics (the
      // header already says "Help", so no separate view title).
      var search = el('div', 'wf-chat-search wf-chat-search--outlined');
      search.appendChild(svg(ICON.search, 'wf-chat-search__icon', { strokeWidth: 1.8 }));
      var filterLabel = el('label', 'wf-chat-sr', 'Search help topics');
      filterLabel.setAttribute('for', 'wf-chat-help-filter');
      var filter = el('input', 'wf-chat-search__input');
      filter.type = 'search';
      filter.id = 'wf-chat-help-filter';
      filter.placeholder = 'Search help topics';
      filter.autocomplete = 'off';
      search.appendChild(filterLabel);
      search.appendChild(filter);
      v.appendChild(search);
      var noMatch = el('p', 'wf-chat-loading wf-chat-help-nomatch', 'No matching topics — try "photo", "return", or "track".');
      noMatch.hidden = true;
      v.appendChild(noMatch);

      // One card per category, in the order the backend serves them
      // (curated sort_order). Uncategorized topics group under ''.
      var groups = [];
      var byCat = {};
      v._faqs.forEach(function (faq) {
        var cat = faq.category || '';
        if (onlyCat && cat !== onlyCat) return;
        if (!Object.prototype.hasOwnProperty.call(byCat, cat)) {
          byCat[cat] = [];
          groups.push(cat);
        }
        byCat[cat].push(faq);
      });
      var groupEls = [];
      groups.forEach(function (cat) {
        var wrap = el('div', 'wf-chat-faq-group');
        if (cat && groups.length > 1) {
          wrap.appendChild(el('h3', 'wf-chat-faq-group__label', cat));
        }
        var box = el('div', 'wf-chat-faq-list');
        var rows = [];
        byCat[cat].forEach(function (faq) {
          var b = el('button', 'wf-chat-faq-row');
          b.type = 'button';
          b.appendChild(el('span', 'wf-chat-faq-row__text', faq.question || ''));
          var c = el('span', 'wf-chat-faq-row__chevron');
          c.appendChild(svg(ICON.chevronRight, null, { strokeWidth: 1.8 }));
          b.appendChild(c);
          b.addEventListener('click', function () { renderHelpArticle(v, faq); });
          box.appendChild(b);
          rows.push({ btn: b, text: [cat, faq.question, faq.answer].join(' ').toLowerCase() });
        });
        wrap.appendChild(box);
        v.appendChild(wrap);
        groupEls.push({ wrap: wrap, rows: rows });
      });

      filter.addEventListener('input', debounce(function () {
        var q = filter.value.trim().toLowerCase();
        var shown = 0;
        groupEls.forEach(function (g) {
          var hits = 0;
          g.rows.forEach(function (r) {
            var hit = !q || r.text.indexOf(q) !== -1;
            r.btn.hidden = !hit;
            if (hit) hits++;
          });
          g.wrap.hidden = !hits;
          shown += hits;
        });
        noMatch.hidden = shown > 0;
        if (q) announce(shown ? shown + (shown === 1 ? ' topic' : ' topics') : 'No matching topics');
      }, 150));

      if (onlyCat) {
        var all = el('button', 'wf-chat-btn wf-chat-btn--secondary wf-chat-help-all', 'See all help topics');
        all.type = 'button';
        all.addEventListener('click', function () {
          helpCategory = null;
          renderHelpList(v);
          setTimeout(function () { focusFirst(v); }, 0);
        });
        v.appendChild(all);
      }
    }
    // Bottom CTA card
    var cta = el('div', 'wf-chat-help-cta');
    cta.appendChild(el('p', 'wf-chat-help-cta__text', "Can't find your answer?"));
    var btn = el('button', 'wf-chat-btn wf-chat-btn--primary');
    btn.type = 'button';
    btn.textContent = ctaLabel();
    btn.addEventListener('click', function () { setView(ctaView()); });
    cta.appendChild(btn);
    v.appendChild(cta);
  }

  function renderHelpArticle(v, faq) {
    helpScreen = 'article';
    v.textContent = '';

    var card = el('div', 'wf-chat-faq-article');
    var title = el('h3', 'wf-chat-faq-article__title', faq.question || '');
    card.appendChild(title);
    var body = el('div', 'wf-chat-faq-article__body');
    renderPlainText(body, faq.answer || '');
    card.appendChild(body);

    // "Was this helpful? (Yes) (No)" — lightweight, client-side.
    var fb = el('div', 'wf-chat-faq-feedback');
    fb.appendChild(el('span', 'wf-chat-faq-feedback__q', 'Was this helpful?'));
    var yes = el('button', 'wf-chat-pill', 'Yes');
    yes.type = 'button';
    var no = el('button', 'wf-chat-pill', 'No');
    no.type = 'button';
    yes.addEventListener('click', function () {
      fb.textContent = 'Thanks for the feedback!';
      announce('Thanks for the feedback!');
    });
    no.addEventListener('click', function () {
      fb.textContent = "Sorry about that — send us a message and we'll help.";
      announce("Sorry about that — send us a message and we'll help.");
      try { msgBtn.focus(); } catch (e) {}
    });
    fb.appendChild(yes);
    fb.appendChild(no);
    card.appendChild(fb);
    v.appendChild(card);

    v.appendChild(el('p', 'wf-chat-still-help', 'Still need help?'));
    var msgBtn = el('button', 'wf-chat-btn wf-chat-btn--primary');
    msgBtn.type = 'button';
    msgBtn.textContent = ONLINE ? 'Send us a message' : 'Leave a message';
    msgBtn.addEventListener('click', function () {
      startNewConversation();
      setView(ctaView());
    });
    v.appendChild(msgBtn);

    var okBtn = el('button', 'wf-chat-btn wf-chat-btn--secondary');
    okBtn.type = 'button';
    okBtn.textContent = 'Back to topics';
    okBtn.addEventListener('click', function () { renderHelpList(v); });
    v.appendChild(okBtn);

    setTimeout(function () {
      title.setAttribute('tabindex', '-1');
      try { title.focus(); } catch (e) {}
    }, 0);
  }

  function matchesFaq(faq, expandId) {
    if (faq.id != null && expandId.id != null) return String(faq.id) === String(expandId.id);
    return faq.question && expandId.question && faq.question === expandId.question;
  }

  // Open the Help view with one FAQ pre-expanded (used by search results).
  function openHelpWithFaq(faq) {
    setView('help', { faq: faq });
  }

  /*==========================================================================
    ORDER view — OTP verification flow (Gorgias/Richpanel style).
      contact screen -> code screen -> order-list screen -> timeline detail.
    A 30-min verify session (wf_chat_session) survives view switches; a 401
    from the list endpoint means it expired -> back to the contact screen.
  ==========================================================================*/
  var orderEls = {};        // holds the order view container + resend timer state
  var orderScreen = '';     // which order-flow screen is showing (for goBack)

  function buildOrderView() {
    var v = makeView('order', false);
    orderEls.view = v;
    // Entering the order view: if a session already exists, jump to the list;
    // otherwise start at the contact screen.
    v._reset = function () {
      stopResendTimer();
      var session = sess.get('wf_chat_session');
      if (session) {
        renderOrderListLoading(v, session);
      } else {
        renderContactScreen(v);
      }
    };
    // Header back arrow steps BACK through the flow hierarchy
    // (track/help -> order info -> list, code -> contact) before leaving the
    // order view. Returns true when the step was handled here.
    v._back = function () {
      var session = sess.get('wf_chat_session');
      var helpChildren = ['dbnr', 'addr', 'cancelinfo', 'returnsinfo', 'refundinfo'];
      if (helpChildren.indexOf(orderScreen) !== -1 && orderEls.current) {
        renderOrderHelp(v, orderEls.current);
        return true;
      }
      if ((orderScreen === 'track' || orderScreen === 'orderhelp') && orderEls.current) {
        renderOrderDetail(v, orderEls.current);
        return true;
      }
      if (orderScreen === 'detail' || orderScreen === 'track' || orderScreen === 'orderhelp') {
        if (session) renderOrderListLoading(v, session);
        else renderContactScreen(v);
        return true;
      }
      if (orderScreen === 'code') {
        renderContactScreen(v);
        return true;
      }
      return false;
    };
  }

  function orderTitle(container, text) {
    container.appendChild(el('h3', 'wf-chat-section-title', text || 'Help with your orders'));
  }

  // Worldwide country calling codes: [iso2, name, dial]. US pinned first
  // (store default), the rest alphabetical. NANP territories share +1.
  var CC_LIST = [
    ['US', 'United States', '1'],
    ['AF', 'Afghanistan', '93'], ['AL', 'Albania', '355'], ['DZ', 'Algeria', '213'],
    ['AS', 'American Samoa', '1'], ['AD', 'Andorra', '376'], ['AO', 'Angola', '244'],
    ['AI', 'Anguilla', '1'], ['AG', 'Antigua & Barbuda', '1'], ['AR', 'Argentina', '54'],
    ['AM', 'Armenia', '374'], ['AW', 'Aruba', '297'], ['AU', 'Australia', '61'],
    ['AT', 'Austria', '43'], ['AZ', 'Azerbaijan', '994'], ['BS', 'Bahamas', '1'],
    ['BH', 'Bahrain', '973'], ['BD', 'Bangladesh', '880'], ['BB', 'Barbados', '1'],
    ['BY', 'Belarus', '375'], ['BE', 'Belgium', '32'], ['BZ', 'Belize', '501'],
    ['BJ', 'Benin', '229'], ['BM', 'Bermuda', '1'], ['BT', 'Bhutan', '975'],
    ['BO', 'Bolivia', '591'], ['BA', 'Bosnia & Herzegovina', '387'], ['BW', 'Botswana', '267'],
    ['BR', 'Brazil', '55'], ['VG', 'British Virgin Islands', '1'], ['BN', 'Brunei', '673'],
    ['BG', 'Bulgaria', '359'], ['BF', 'Burkina Faso', '226'], ['BI', 'Burundi', '257'],
    ['KH', 'Cambodia', '855'], ['CM', 'Cameroon', '237'], ['CA', 'Canada', '1'],
    ['CV', 'Cape Verde', '238'], ['KY', 'Cayman Islands', '1'], ['CF', 'Central African Republic', '236'],
    ['TD', 'Chad', '235'], ['CL', 'Chile', '56'], ['CN', 'China', '86'],
    ['CO', 'Colombia', '57'], ['KM', 'Comoros', '269'], ['CG', 'Congo', '242'],
    ['CD', 'Congo (DRC)', '243'], ['CK', 'Cook Islands', '682'], ['CR', 'Costa Rica', '506'],
    ['CI', "Cote d'Ivoire", '225'], ['HR', 'Croatia', '385'], ['CU', 'Cuba', '53'],
    ['CW', 'Curacao', '599'], ['CY', 'Cyprus', '357'], ['CZ', 'Czechia', '420'],
    ['DK', 'Denmark', '45'], ['DJ', 'Djibouti', '253'], ['DM', 'Dominica', '1'],
    ['DO', 'Dominican Republic', '1'], ['EC', 'Ecuador', '593'], ['EG', 'Egypt', '20'],
    ['SV', 'El Salvador', '503'], ['GQ', 'Equatorial Guinea', '240'], ['ER', 'Eritrea', '291'],
    ['EE', 'Estonia', '372'], ['SZ', 'Eswatini', '268'], ['ET', 'Ethiopia', '251'],
    ['FJ', 'Fiji', '679'], ['FI', 'Finland', '358'], ['FR', 'France', '33'],
    ['PF', 'French Polynesia', '689'], ['GA', 'Gabon', '241'], ['GM', 'Gambia', '220'],
    ['GE', 'Georgia', '995'], ['DE', 'Germany', '49'], ['GH', 'Ghana', '233'],
    ['GI', 'Gibraltar', '350'], ['GR', 'Greece', '30'], ['GL', 'Greenland', '299'],
    ['GD', 'Grenada', '1'], ['GU', 'Guam', '1'], ['GT', 'Guatemala', '502'],
    ['GN', 'Guinea', '224'], ['GW', 'Guinea-Bissau', '245'], ['GY', 'Guyana', '592'],
    ['HT', 'Haiti', '509'], ['HN', 'Honduras', '504'], ['HK', 'Hong Kong', '852'],
    ['HU', 'Hungary', '36'], ['IS', 'Iceland', '354'], ['IN', 'India', '91'],
    ['ID', 'Indonesia', '62'], ['IR', 'Iran', '98'], ['IQ', 'Iraq', '964'],
    ['IE', 'Ireland', '353'], ['IL', 'Israel', '972'], ['IT', 'Italy', '39'],
    ['JM', 'Jamaica', '1'], ['JP', 'Japan', '81'], ['JO', 'Jordan', '962'],
    ['KZ', 'Kazakhstan', '7'], ['KE', 'Kenya', '254'], ['KI', 'Kiribati', '686'],
    ['XK', 'Kosovo', '383'], ['KW', 'Kuwait', '965'], ['KG', 'Kyrgyzstan', '996'],
    ['LA', 'Laos', '856'], ['LV', 'Latvia', '371'], ['LB', 'Lebanon', '961'],
    ['LS', 'Lesotho', '266'], ['LR', 'Liberia', '231'], ['LY', 'Libya', '218'],
    ['LI', 'Liechtenstein', '423'], ['LT', 'Lithuania', '370'], ['LU', 'Luxembourg', '352'],
    ['MO', 'Macao', '853'], ['MG', 'Madagascar', '261'], ['MW', 'Malawi', '265'],
    ['MY', 'Malaysia', '60'], ['MV', 'Maldives', '960'], ['ML', 'Mali', '223'],
    ['MT', 'Malta', '356'], ['MH', 'Marshall Islands', '692'], ['MR', 'Mauritania', '222'],
    ['MU', 'Mauritius', '230'], ['MX', 'Mexico', '52'], ['FM', 'Micronesia', '691'],
    ['MD', 'Moldova', '373'], ['MC', 'Monaco', '377'], ['MN', 'Mongolia', '976'],
    ['ME', 'Montenegro', '382'], ['MS', 'Montserrat', '1'], ['MA', 'Morocco', '212'],
    ['MZ', 'Mozambique', '258'], ['MM', 'Myanmar', '95'], ['NA', 'Namibia', '264'],
    ['NR', 'Nauru', '674'], ['NP', 'Nepal', '977'], ['NL', 'Netherlands', '31'],
    ['NC', 'New Caledonia', '687'], ['NZ', 'New Zealand', '64'], ['NI', 'Nicaragua', '505'],
    ['NE', 'Niger', '227'], ['NG', 'Nigeria', '234'], ['MK', 'North Macedonia', '389'],
    ['MP', 'Northern Mariana Islands', '1'], ['NO', 'Norway', '47'], ['OM', 'Oman', '968'],
    ['PK', 'Pakistan', '92'], ['PW', 'Palau', '680'], ['PS', 'Palestine', '970'],
    ['PA', 'Panama', '507'], ['PG', 'Papua New Guinea', '675'], ['PY', 'Paraguay', '595'],
    ['PE', 'Peru', '51'], ['PH', 'Philippines', '63'], ['PL', 'Poland', '48'],
    ['PT', 'Portugal', '351'], ['PR', 'Puerto Rico', '1'], ['QA', 'Qatar', '974'],
    ['RO', 'Romania', '40'], ['RU', 'Russia', '7'], ['RW', 'Rwanda', '250'],
    ['WS', 'Samoa', '685'], ['SM', 'San Marino', '378'], ['ST', 'Sao Tome & Principe', '239'],
    ['SA', 'Saudi Arabia', '966'], ['SN', 'Senegal', '221'], ['RS', 'Serbia', '381'],
    ['SC', 'Seychelles', '248'], ['SL', 'Sierra Leone', '232'], ['SG', 'Singapore', '65'],
    ['SX', 'Sint Maarten', '1'], ['SK', 'Slovakia', '421'], ['SI', 'Slovenia', '386'],
    ['SB', 'Solomon Islands', '677'], ['SO', 'Somalia', '252'], ['ZA', 'South Africa', '27'],
    ['KR', 'South Korea', '82'], ['SS', 'South Sudan', '211'], ['ES', 'Spain', '34'],
    ['LK', 'Sri Lanka', '94'], ['KN', 'St. Kitts & Nevis', '1'], ['LC', 'St. Lucia', '1'],
    ['VC', 'St. Vincent & Grenadines', '1'], ['SD', 'Sudan', '249'], ['SR', 'Suriname', '597'],
    ['SE', 'Sweden', '46'], ['CH', 'Switzerland', '41'], ['SY', 'Syria', '963'],
    ['TW', 'Taiwan', '886'], ['TJ', 'Tajikistan', '992'], ['TZ', 'Tanzania', '255'],
    ['TH', 'Thailand', '66'], ['TL', 'Timor-Leste', '670'], ['TG', 'Togo', '228'],
    ['TO', 'Tonga', '676'], ['TT', 'Trinidad & Tobago', '1'], ['TN', 'Tunisia', '216'],
    ['TR', 'Turkey', '90'], ['TM', 'Turkmenistan', '993'], ['TC', 'Turks & Caicos', '1'],
    ['TV', 'Tuvalu', '688'], ['UG', 'Uganda', '256'], ['UA', 'Ukraine', '380'],
    ['AE', 'United Arab Emirates', '971'], ['GB', 'United Kingdom', '44'],
    ['UY', 'Uruguay', '598'], ['VI', 'US Virgin Islands', '1'], ['UZ', 'Uzbekistan', '998'],
    ['VU', 'Vanuatu', '678'], ['VE', 'Venezuela', '58'], ['VN', 'Vietnam', '84'],
    ['YE', 'Yemen', '967'], ['ZM', 'Zambia', '260'], ['ZW', 'Zimbabwe', '263']
  ];

  // ISO code -> flag emoji (regional indicator pair). Windows renders these
  // as letter pairs, which still read fine; mobile shows real flags.
  function ccFlag(iso) {
    return String.fromCodePoint(0x1F1E6 + iso.charCodeAt(0) - 65,
                                0x1F1E6 + iso.charCodeAt(1) - 65);
  }

  // Searchable country-code picker (button -> search + listbox popover),
  // WAI combobox pattern: arrows move, Enter selects, Escape closes back
  // to the button, outside click dismisses.
  function ccPicker() {
    var dial = '+1';
    var root = el('div', 'wf-chat-cc');

    var btn = el('button', 'wf-chat-cc__btn');
    btn.type = 'button';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-label', 'Country code: +1 United States');
    var btnFlag = el('span', 'wf-chat-cc__flag', ccFlag('US'));
    btnFlag.setAttribute('aria-hidden', 'true');
    var btnCode = el('span', 'wf-chat-cc__code', '+1');
    var btnChev = el('span', 'wf-chat-cc__chev');
    btnChev.appendChild(svg(ICON.chevronDown, null, { strokeWidth: 1.8 }));
    btn.appendChild(btnFlag);
    btn.appendChild(btnCode);
    btn.appendChild(btnChev);
    root.appendChild(btn);

    var pop = el('div', 'wf-chat-cc__pop');
    pop.hidden = true;
    var search = el('input', 'wf-chat-cc__search');
    search.type = 'text';
    search.placeholder = 'Search countries';
    search.autocomplete = 'off';
    search.setAttribute('role', 'combobox');
    search.setAttribute('aria-label', 'Search countries');
    search.setAttribute('aria-controls', 'wf-chat-cc-list');
    search.setAttribute('aria-expanded', 'true');
    pop.appendChild(search);
    var list = el('ul', 'wf-chat-cc__list');
    list.id = 'wf-chat-cc-list';
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', 'Country codes');
    pop.appendChild(list);
    root.appendChild(pop);

    var options = [];
    var active = -1;
    var selected = 0;

    CC_LIST.forEach(function (c, i) {
      var li = el('li', 'wf-chat-cc__opt');
      li.setAttribute('role', 'option');
      li.id = 'wf-chat-cc-opt-' + i;
      li.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      var flag = el('span', 'wf-chat-cc__flag', ccFlag(c[0]));
      flag.setAttribute('aria-hidden', 'true');
      li.appendChild(flag);
      li.appendChild(el('span', 'wf-chat-cc__name', c[1]));
      li.appendChild(el('span', 'wf-chat-cc__dial', '+' + c[2]));
      li.addEventListener('click', function () { choose(i); });
      list.appendChild(li);
      options.push({ li: li, name: c[1].toLowerCase(), dial: '+' + c[2] });
    });

    function visibleIdx() {
      var out = [];
      for (var i = 0; i < options.length; i++) if (!options[i].li.hidden) out.push(i);
      return out;
    }
    function setActive(i) {
      if (active >= 0) options[active].li.classList.remove('wf-chat-cc__opt--active');
      active = i;
      if (i >= 0) {
        options[i].li.classList.add('wf-chat-cc__opt--active');
        search.setAttribute('aria-activedescendant', options[i].li.id);
        try { options[i].li.scrollIntoView({ block: 'nearest' }); } catch (e) {}
      } else {
        search.removeAttribute('aria-activedescendant');
      }
    }
    function filter() {
      var q = search.value.trim().toLowerCase();
      var qd = q.replace(/[^\d]/g, '');
      options.forEach(function (o) {
        o.li.hidden = !(q === '' || o.name.indexOf(q) !== -1 ||
                        (qd !== '' && o.dial.slice(1).indexOf(qd) === 0));
      });
      var vis = visibleIdx();
      setActive(vis.length ? vis[0] : -1);
    }
    search.addEventListener('input', filter);

    function onDocDown(e) { if (!root.contains(e.target)) close(false); }
    function open() {
      pop.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      search.value = '';
      filter();
      setActive(selected);
      document.addEventListener('mousedown', onDocDown, true);
      setTimeout(function () { try { search.focus(); } catch (e) {} }, 0);
    }
    function close(refocus) {
      if (pop.hidden) return;
      pop.hidden = true;
      btn.setAttribute('aria-expanded', 'false');
      document.removeEventListener('mousedown', onDocDown, true);
      if (refocus) { try { btn.focus(); } catch (e) {} }
    }
    function choose(i) {
      dial = options[i].dial;
      options[selected].li.setAttribute('aria-selected', 'false');
      selected = i;
      options[i].li.setAttribute('aria-selected', 'true');
      btnFlag.textContent = ccFlag(CC_LIST[i][0]);
      btnCode.textContent = dial;
      btn.setAttribute('aria-label', 'Country code: ' + dial + ' ' + CC_LIST[i][1]);
      announce('Country code ' + dial + ' ' + CC_LIST[i][1] + ' selected.');
      close(true);
    }

    btn.addEventListener('click', function () {
      if (pop.hidden) open(); else close(true);
    });
    search.addEventListener('keydown', function (e) {
      var vis = visibleIdx();
      var pos = vis.indexOf(active);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (vis.length) setActive(vis[Math.min(pos + 1, vis.length - 1)]);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (vis.length) setActive(vis[Math.max(pos - 1, 0)]);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (active >= 0) choose(active);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();   /* close the popover, not the whole widget */
        close(true);
      } else if (e.key === 'Tab') {
        close(false);
      }
    });

    return { root: root, getDial: function () { return dial; } };
  }

  // ---- (a) CONTACT screen -------------------------------------------------
  function renderContactScreen(v, prefill) {
    orderScreen = 'contact';
    stopResendTimer();
    v.textContent = '';
    var tile = el('span', 'wf-chat-icon-tile');
    tile.appendChild(svg(ICON.box, null, { strokeWidth: 1.8 }));
    v.appendChild(tile);
    orderTitle(v, 'Find your orders');
    v.appendChild(el('p', 'wf-chat-intro',
      "Enter the email or phone number from your order and we'll send you a verification code."));

    var form = el('form', 'wf-chat-form');
    form.setAttribute('novalidate', 'novalidate');
    var contactField = field('Email or phone', 'wf-chat-verify-contact', 'text', 'you@email.com');

    // Richpanel-style country code picker: hidden for emails, revealed as
    // soon as the value looks like a phone number. The selected code is
    // prepended on submit unless the visitor already typed their own +.
    var cc = ccPicker();
    cc.root.style.display = 'none';

    function looksPhone(val) {
      var s = String(val || '').trim();
      // 3+ digits before treating it as a phone — keeps the country-code
      // picker from flashing in on the first keystroke or digit-led emails.
      return s.indexOf('@') === -1 && /^[+(\d]/.test(s) &&
             !/[a-z]/i.test(s) && s.replace(/\D/g, '').length >= 3;
    }
    function syncCc() {
      var s = contactField.input.value.trim();
      cc.root.style.display = (looksPhone(s) && s.charAt(0) !== '+') ? '' : 'none';
    }
    contactField.input.addEventListener('input', syncCc);

    var contactRow = el('div', 'wf-chat-contact-row');
    contactRow.appendChild(cc.root);
    contactRow.appendChild(contactField.input);
    contactField.wrap.appendChild(contactRow);

    if (prefill) { contactField.input.value = prefill; syncCc(); }
    var err = el('div', 'wf-chat-error');
    err.setAttribute('role', 'alert');
    err.style.display = 'none';
    form.appendChild(contactField.wrap);
    form.appendChild(err);
    var submit = primaryButton('Send code', true);
    form.appendChild(submit.btn);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      err.style.display = 'none';
      var contact = contactField.input.value.trim();
      if (!contact) {
        err.textContent = 'Enter the email or phone from your order.';
        err.style.display = 'block';
        return;
      }
      // Auto-convert phone entries to international format: +1 (or the
      // selected code) + digits, matching what Shopify stores.
      if (looksPhone(contact) && contact.charAt(0) !== '+') {
        contact = cc.getDial() + contact.replace(/\D/g, '');
      }
      submit.setLoading(true);
      postJSON('/support/widget/verify/start/', { contact: contact }).then(function (res) {
        submit.setLoading(false);
        if (res.ok && res.body && res.body.ok) {
          announce('Verification code sent.');
          renderCodeScreen(v, contact, res.body.sent_to || contact, res.body.channel);
        } else {
          err.textContent = (res.body && res.body.message) || 'We could not send a code. Please try again.';
          err.style.display = 'block';
          announce(err.textContent);
        }
      }).catch(function () {
        submit.setLoading(false);
        err.textContent = 'Something went wrong. Please try again.';
        err.style.display = 'block';
      });
    });

    v.appendChild(form);

    var alt = el('p', 'wf-chat-order-alt');
    alt.appendChild(document.createTextNode('Can’t find it? '));
    var altBtn = el('button', 'wf-chat-linkbtn', ctaLabel());
    altBtn.type = 'button';
    altBtn.addEventListener('click', function () { setView(ctaView()); });
    alt.appendChild(altBtn);
    v.appendChild(alt);

    setTimeout(function () { try { contactField.input.focus(); } catch (e) {} }, 0);
  }

  // ---- (b) CODE screen ----------------------------------------------------
  function renderCodeScreen(v, contact, sentTo, channel) {
    orderScreen = 'code';
    var isSms = channel === 'sms';
    stopResendTimer();
    v.textContent = '';
    orderTitle(v);
    v.appendChild(el('h3', 'wf-chat-verify-heading', 'Enter the code we sent to ' + sentTo));

    // "Not your email? Change" — Change goes back to the contact screen.
    var changeRow = el('p', 'wf-chat-verify-change');
    changeRow.appendChild(document.createTextNode(isSms ? 'Not your number? ' : 'Not your email? '));
    var changeBtn = el('button', 'wf-chat-linkbtn', 'Change');
    changeBtn.type = 'button';
    changeBtn.addEventListener('click', function () { renderContactScreen(v, contact); });
    changeRow.appendChild(changeBtn);
    v.appendChild(changeRow);

    // Six single-digit code boxes.
    var boxesWrap = el('div', 'wf-chat-code');
    boxesWrap.setAttribute('role', 'group');
    boxesWrap.setAttribute('aria-label', 'Verification code');
    var inputs = [];
    for (var i = 0; i < 6; i++) {
      (function (idx) {
        var inp = el('input', 'wf-chat-code__box');
        inp.type = 'text';
        inp.inputMode = 'numeric';
        inp.autocomplete = idx === 0 ? 'one-time-code' : 'off';
        inp.maxLength = 1;
        inp.setAttribute('aria-label', 'Digit ' + (idx + 1) + ' of 6');
        inp.addEventListener('input', function () {
          // Keep digits only; auto-advance on entry.
          inp.value = inp.value.replace(/\D/g, '').slice(0, 1);
          if (inp.value && idx < 5) { try { inputs[idx + 1].focus(); } catch (e) {} }
        });
        inp.addEventListener('keydown', function (e) {
          if (e.key === 'Backspace' && !inp.value && idx > 0) {
            e.preventDefault();
            try { inputs[idx - 1].focus(); inputs[idx - 1].value = ''; } catch (err2) {}
          } else if (e.key === 'ArrowLeft' && idx > 0) {
            e.preventDefault(); try { inputs[idx - 1].focus(); } catch (err2) {}
          } else if (e.key === 'ArrowRight' && idx < 5) {
            e.preventDefault(); try { inputs[idx + 1].focus(); } catch (err2) {}
          }
        });
        inp.addEventListener('paste', function (e) {
          var text = (e.clipboardData || window.clipboardData).getData('text') || '';
          var digits = text.replace(/\D/g, '').slice(0, 6);
          if (!digits) return;
          e.preventDefault();
          for (var j = 0; j < 6; j++) { inputs[j].value = digits[j] || ''; }
          var next = Math.min(digits.length, 5);
          try { inputs[next].focus(); } catch (err2) {}
        });
        inputs.push(inp);
        boxesWrap.appendChild(inp);
      })(i);
    }
    v.appendChild(boxesWrap);

    // Spam-folder note (email) / delivery note (SMS).
    v.appendChild(el('p', 'wf-chat-verify-note', isSms
      ? "Don't see the text? It can take a minute to arrive — or try resending."
      : "Don't see the message in your inbox? Please check your spam folder or try resending."));

    // Resend countdown -> live "Resend code" button.
    var resend = el('button', 'wf-chat-verify-resend', 'Resend in 60s');
    resend.type = 'button';
    resend.disabled = true;
    resend.addEventListener('click', function () {
      if (resend.disabled) return;
      resend.disabled = true;
      resend.textContent = 'Sending…';
      postJSON('/support/widget/verify/start/', { contact: contact }).then(function (res) {
        if (res.ok && res.body && res.body.ok) {
          announce('Verification code re-sent.');
          if (res.body.channel && res.body.channel !== channel) {
            // e.g. SMS fell back to email — redraw so the copy matches.
            renderCodeScreen(v, contact, res.body.sent_to || contact, res.body.channel);
            return;
          }
          startResendTimer(resend);
        } else {
          // e.g. 429 rate_limited — show the server message and re-enable.
          err.textContent = (res.body && res.body.message) || 'Could not resend the code yet.';
          err.style.display = 'block';
          resend.disabled = false;
          resend.textContent = 'Resend code';
        }
      }).catch(function () {
        resend.disabled = false;
        resend.textContent = 'Resend code';
      });
    });
    v.appendChild(resend);

    var err = el('div', 'wf-chat-error');
    err.setAttribute('role', 'alert');
    err.style.display = 'none';
    v.appendChild(err);

    var submit = primaryButton('Verify', true);
    v.appendChild(submit.btn);

    submit.btn.type = 'button';
    submit.btn.addEventListener('click', function () {
      err.style.display = 'none';
      var code = inputs.map(function (x) { return x.value; }).join('');
      if (code.length !== 6) {
        err.textContent = 'Enter the 6-digit code.';
        err.style.display = 'block';
        return;
      }
      submit.setLoading(true);
      postJSON('/support/widget/verify/check/', { contact: contact, code: code }).then(function (res) {
        submit.setLoading(false);
        if (res.ok && res.body && res.body.ok && res.body.session) {
          sess.set('wf_chat_session', res.body.session);
          // The API only returns the MASKED email; but if the user typed an
          // email as their contact, stash the raw one so a verified chat can
          // skip the pre-chat form and send it as customer_email. (Phone
          // contacts leave this unset — we can't derive an email.)
          if (isEmail(contact)) sess.set('wf_chat_email_verified', contact);
          announce('Verified.');
          renderOrderList(v, res.body.orders || [], res.body.email || sentTo);
        } else {
          err.textContent = (res.body && res.body.message) || 'That code was incorrect. Please try again.';
          err.style.display = 'block';
          announce(err.textContent);
          // Clear boxes + focus the first, per spec.
          inputs.forEach(function (x) { x.value = ''; });
          try { inputs[0].focus(); } catch (e) {}
        }
      }).catch(function () {
        submit.setLoading(false);
        err.textContent = 'Something went wrong. Please try again.';
        err.style.display = 'block';
      });
    });

    startResendTimer(resend);
    setTimeout(function () { try { inputs[0].focus(); } catch (e) {} }, 0);
  }

  // 60s resend countdown; button becomes a live "Resend code" at zero.
  function startResendTimer(resendBtn) {
    stopResendTimer();
    var remaining = 60;
    resendBtn.disabled = true;
    resendBtn.textContent = 'Resend in ' + remaining + 's';
    orderEls.resendTimer = setInterval(function () {
      remaining -= 1;
      if (remaining <= 0) {
        stopResendTimer();
        resendBtn.disabled = false;
        resendBtn.textContent = 'Resend code';
      } else {
        resendBtn.textContent = 'Resend in ' + remaining + 's';
      }
    }, 1000);
  }
  function stopResendTimer() {
    if (orderEls.resendTimer) { clearInterval(orderEls.resendTimer); orderEls.resendTimer = null; }
  }

  // ---- (c) ORDER LIST screen ---------------------------------------------
  function renderOrderListLoading(v, session) {
    orderScreen = 'list';
    stopResendTimer();
    v.textContent = '';
    orderTitle(v);
    var skel = el('div', 'wf-chat-order-skel');
    var g = skelGroup(skel, 'Loading your orders…');
    g.appendChild(skelBar('wf-chat-skel--block'));
    g.appendChild(skelBar('wf-chat-skel--block'));
    v.appendChild(skel);
    getJSON('/support/widget/verify/orders/?session=' + encodeURIComponent(session)).then(function (res) {
      if (res.status === 401) {
        // Session expired — drop it and go back to the contact screen.
        sess.del('wf_chat_session');
        renderContactScreen(v);
        return;
      }
      if (res.ok && res.body && res.body.orders) {
        renderOrderList(v, res.body.orders, res.body.email || '');
      } else {
        renderContactScreen(v);
      }
    }).catch(function () {
      renderContactScreen(v);
    });
  }

  function renderOrderList(v, orders, email) {
    orderScreen = 'list';
    stopResendTimer();
    v.textContent = '';
    orderTitle(v);
    if (email) v.appendChild(el('p', 'wf-chat-signed-in', 'Signed in as ' + email));

    if (!orders.length) {
      v.appendChild(el('p', 'wf-chat-intro', 'We could not find any orders on this account.'));
    } else {
      announce(orders.length + (orders.length === 1 ? ' order' : ' orders') + ' found.');
      orders.forEach(function (order) {
        v.appendChild(orderCard(v, order));
      });
      var viewAll = el('a', 'wf-chat-view-all', 'View all orders');
      viewAll.href = '/account';
      viewAll.target = '_blank';
      viewAll.rel = 'noopener';
      viewAll.appendChild(srSpan(' (opens in a new window)'));
      v.appendChild(viewAll);
    }

    // Bottom "Not seeing your order? Chat with us" link.
    var help = el('p', 'wf-chat-order-help');
    help.appendChild(document.createTextNode('Not seeing your order? '));
    var link = el('button', 'wf-chat-linkbtn', ONLINE ? 'Chat with us' : 'Leave a message');
    link.type = 'button';
    link.addEventListener('click', function () { setView(ctaView()); });
    help.appendChild(link);
    v.appendChild(help);

    // Move focus to the list heading for keyboard/SR users.
    setTimeout(function () {
      var h = v.querySelector('.wf-chat-section-title');
      if (h) { h.setAttribute('tabindex', '-1'); try { h.focus(); } catch (e) {} }
    }, 0);
  }

  // Richpanel-style order card: "Order #… · N items" header row (whole area
  // opens the detail), product thumb + title + status pill, Amount/Date
  // columns, then outlined Help / Track buttons. Buttons hold spans only.
  function orderCard(v, order, opts) {
    opts = opts || {};
    var card = el('div', 'wf-chat-order-card');
    var items = order.line_items || [];
    var count = items.reduce(function (n, it) { return n + (it.quantity || 1); }, 0);

    var main = el('button', 'wf-chat-order-card__main');
    main.type = 'button';

    var top = el('span', 'wf-chat-order-card__toprow');
    top.appendChild(el('span', 'wf-chat-order-card__num', 'Order ' + (order.name || '')));
    if (count) {
      var dot = el('span', 'wf-chat-order-card__dot', '·');
      dot.setAttribute('aria-hidden', 'true');
      top.appendChild(dot);
      top.appendChild(el('span', 'wf-chat-order-card__count',
        count + (count === 1 ? ' item' : ' items')));
    }
    var chev = el('span', 'wf-chat-order-card__chevron');
    chev.appendChild(svg(ICON.chevronRight, null, { strokeWidth: 1.8 }));
    top.appendChild(chev);
    main.appendChild(top);

    var body = el('span', 'wf-chat-order-card__body');
    var imgSrc = '';
    for (var i = 0; i < items.length; i++) {
      if (items[i].image) { imgSrc = items[i].image; break; }
    }
    if (imgSrc) {
      var img = el('img', 'wf-chat-order-card__thumb');
      img.src = thumbUrl(imgSrc, 168);
      img.alt = '';
      img.loading = 'lazy';
      img.width = 84;
      img.height = 84;
      body.appendChild(img);
    } else {
      body.appendChild(el('span', 'wf-chat-order-card__thumb wf-chat-order-card__thumb--tile', 'WF'));
    }
    var prod = el('span', 'wf-chat-order-card__prod');
    prod.appendChild(el('span', 'wf-chat-order-card__title',
      (items[0] && items[0].title) || 'Your order'));
    prod.appendChild(statusChip(order));
    body.appendChild(prod);
    main.appendChild(body);

    if ((opts.mode || 'list') === 'list') {
      var facts = el('span', 'wf-chat-order-card__facts');
      var total = fmtMoney(order.total_price, order.currency);
      if (total) {
        var f1 = el('span', 'wf-chat-order-card__fact');
        f1.appendChild(el('span', 'wf-chat-order-card__fact-label', 'Amount'));
        f1.appendChild(el('span', 'wf-chat-order-card__fact-value', total));
        facts.appendChild(f1);
      }
      var placed = fmtDate(order.created_at);
      if (placed) {
        var f2 = el('span', 'wf-chat-order-card__fact');
        f2.appendChild(el('span', 'wf-chat-order-card__fact-label', 'Date'));
        f2.appendChild(el('span', 'wf-chat-order-card__fact-value', placed));
        facts.appendChild(f2);
      }
      if (facts.childNodes.length) main.appendChild(facts);
    }

    main.appendChild(srSpan(' — view order details'));
    main.addEventListener('click', function () { renderOrderDetail(v, order); });
    card.appendChild(main);

    var isHelpScreen = opts.mode === 'helpscreen';
    var row = el('div', 'wf-chat-order-card__btnrow');
    var leftBtn = el('button', 'wf-chat-order-card__btn', isHelpScreen ? 'View' : 'Help');
    leftBtn.type = 'button';
    leftBtn.addEventListener('click', function () {
      if (isHelpScreen) renderOrderDetail(v, order);
      else renderOrderHelp(v, order);
    });
    var trackBtn = el('button', 'wf-chat-order-card__btn', 'Track');
    trackBtn.type = 'button';
    trackBtn.addEventListener('click', function () { renderOrderTrack(v, order); });
    row.appendChild(leftBtn);
    row.appendChild(trackBtn);
    card.appendChild(row);

    return card;
  }

  // ---- (c2) HELP-WITH-THIS-ORDER screen (Richpanel style) -----------------
  // Compact order card + common intents. "Where is my order?" opens the
  // timeline; the others drop into chat with the order attached and the
  // request pre-typed so the customer just hits send.
  function renderOrderHelp(v, order) {
    orderScreen = 'orderhelp';
    orderEls.current = order;
    stopResendTimer();
    v.textContent = '';
    orderTitle(v);
    v.appendChild(orderCard(v, order, { mode: 'helpscreen' }));
    v.appendChild(el('h3', 'wf-chat-order-subtitle', 'Help with this order'));

    var box = el('div', 'wf-chat-order-intents');
    [
      { label: 'Where is my order?',
        go: function () { renderOrderTrack(v, order); } },
      { label: "I didn't receive my delivery",
        go: function () { renderOrderDbnr(v, order); } },
      { label: 'Change my shipping address',
        go: function () { renderAddressChange(v, order); } },
      { label: 'Cancel my order',
        go: function () { renderCancelInfo(v, order); } },
      { label: 'Problem with an item',
        go: function () { helpChat(order); } },
      { label: 'Returns & exchanges',
        go: function () { renderReturnsInfo(v, order); } },
      { label: "Where's my refund?",
        go: function () { renderRefundInfo(v, order); } },
      { label: 'Something else',
        go: function () { helpChat(order); } }
    ].forEach(function (it) {
      var b = el('button', 'wf-chat-order-intent');
      b.type = 'button';
      b.appendChild(el('span', 'wf-chat-order-intent__label', it.label));
      var c = el('span', 'wf-chat-order-card__chevron');
      c.appendChild(svg(ICON.chevronRight, null, { strokeWidth: 1.8 }));
      b.appendChild(c);
      b.addEventListener('click', it.go);
      box.appendChild(b);
    });
    v.appendChild(box);

    setTimeout(function () {
      var h = v.querySelector('.wf-chat-section-title');
      if (h) { h.setAttribute('tabindex', '-1'); try { h.focus(); } catch (e) {} }
    }, 0);
  }

  // Start a FRESH conversation about this order (the active one stays
  // reachable via "Your Conversations") — the order_ref only registers on a
  // conversation's first message, so resuming an old thread would lose the
  // attachment. Composer stays empty; the customer uses their own words.
  // NOTE: startNewConversation() resets orderRef, so attach AFTER it.
  function helpChat(order) {
    startNewConversation();
    state.orderRef = (order && order.order_ref) || null;
    setView(ctaView());
  }

  // ---- Order-state helpers (drive the self-service copy) ------------------
  function orderShipped(order) {
    return order.fulfillment_status === 'fulfilled' ||
           order.fulfillment_status === 'partial' ||
           (order.tracking || []).length > 0;
  }
  function isCustomOrder(order) {
    return order.order_type === 'custom' || order.order_type === 'mixed';
  }
  function daysSince(iso) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / 86400000);
  }
  // Manufacturing high-demand window: Jan 20 – Mar 15.
  function inHighSeason() {
    var n = new Date(), m = n.getMonth(), day = n.getDate();
    return (m === 0 && day >= 20) || m === 1 || (m === 2 && day <= 15);
  }

  // Shared scaffold for the simple self-service info screens: heading,
  // paragraphs, then a prefilled chat CTA. All of them back-navigate to the
  // "Help with this order" screen via the header arrow.
  function orderInfoScreen(v, order, screenKey, title, paragraphs, ctaLabelText) {
    orderScreen = screenKey;
    orderEls.current = order;
    stopResendTimer();
    v.textContent = '';
    orderTitle(v);
    v.appendChild(el('h3', 'wf-chat-order-subtitle', title));
    paragraphs.forEach(function (p) {
      v.appendChild(el('p', 'wf-chat-order-copy', p));
    });
    var cta = el('button', 'wf-chat-btn wf-chat-btn--primary');
    cta.type = 'button';
    cta.textContent = ctaLabelText || (ONLINE ? 'Chat with us' : 'Leave a message');
    cta.addEventListener('click', function () { helpChat(order); });
    v.appendChild(cta);
    setTimeout(function () {
      var h = v.querySelector('.wf-chat-section-title');
      if (h) { h.setAttribute('tabindex', '-1'); try { h.focus(); } catch (e) {} }
    }, 0);
  }

  function renderOrderDbnr(v, order) {
    orderInfoScreen(v, order, 'dbnr', "Didn't receive your delivery?", [
      "Tracking says delivered but nothing's there? Packages are sometimes " +
      'scanned as delivered a little early, or left somewhere unexpected.',
      'Please check around your door and porch, with neighbors, your ' +
      "building's mailroom or front desk, and your local post office — " +
      'most packages turn up within a few days.',
      "If it still hasn't appeared within 7 business days of the delivered " +
      "scan, chat with us and we'll make it right."
    ]);
  }

  function renderCancelInfo(v, order) {
    if (order.cancelled) {
      orderInfoScreen(v, order, 'cancelinfo', 'Cancel my order',
        ['This order has already been cancelled.']);
    } else if (orderShipped(order)) {
      orderInfoScreen(v, order, 'cancelinfo', 'Cancel my order', [
        "This order has already shipped, so it can't be cancelled.",
        'Non-personalized items can be returned within 30 days of delivery — ' +
        "chat with us and we'll help with next steps."
      ]);
    } else if (order.order_type === 'custom') {
      orderInfoScreen(v, order, 'cancelinfo', 'Cancel my order', [
        'Personalized pieces go into production right away, so custom orders ' +
        "can't be changed or cancelled once they're placed.",
        "If something's wrong with your order, chat with us — we'll do our " +
        'best to help.'
      ]);
    } else if (order.order_type === 'mixed') {
      orderInfoScreen(v, order, 'cancelinfo', 'Cancel my order', [
        'The personalized pieces in this order went into production right ' +
        "away and can't be cancelled.",
        'The non-personalized items may still be cancellable before the ' +
        "order ships — chat with us and we'll confirm what's possible."
      ]);
    } else {
      orderInfoScreen(v, order, 'cancelinfo', 'Cancel my order', [
        "This order hasn't shipped yet, so we should be able to cancel it.",
        "Chat with us and we'll confirm your cancellation — refunds go back " +
        'to your original payment method within 1–5 business days.'
      ]);
    }
  }

  function renderReturnsInfo(v, order) {
    orderInfoScreen(v, order, 'returnsinfo', 'Returns & exchanges', [
      'Non-personalized items can be returned or exchanged within 30 days ' +
      'of delivery, in their original condition and packaging.',
      'Refunds are issued to your original payment method once we receive ' +
      'the return, and typically appear within 1–5 business days.',
      "Personalized items are made just for you and can't be returned — but " +
      "if anything's wrong with your piece, chat with us and we'll take care of it."
    ]);
  }

  function renderRefundInfo(v, order) {
    orderInfoScreen(v, order, 'refundinfo', "Where's my refund?", [
      'Refunds are processed within 1–2 days of a cancellation or a ' +
      'received return.',
      'Once processed, the money reaches your original payment method ' +
      'within 1–5 business days, depending on your bank.'
    ]);
  }

  // ---- Self-service address change (writes straight to Shopify) -----------
  var US_STATES = [['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
    ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
    ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'],
    ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'],
    ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'],
    ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'], ['MS', 'Mississippi'],
    ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'],
    ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'],
    ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'],
    ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['PR', 'Puerto Rico'], ['RI', 'Rhode Island'],
    ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'],
    ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'],
    ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming']];
  var CA_PROVINCES = [['AB', 'Alberta'], ['BC', 'British Columbia'], ['MB', 'Manitoba'],
    ['NB', 'New Brunswick'], ['NL', 'Newfoundland and Labrador'], ['NS', 'Nova Scotia'],
    ['NT', 'Northwest Territories'], ['NU', 'Nunavut'], ['ON', 'Ontario'],
    ['PE', 'Prince Edward Island'], ['QC', 'Quebec'], ['SK', 'Saskatchewan'], ['YT', 'Yukon']];

  function renderAddressChange(v, order) {
    if (orderShipped(order)) {
      orderInfoScreen(v, order, 'addr', 'Change my shipping address', [
        'This order has already shipped, so the address can only be changed ' +
        'by the carrier.',
        "Chat with us and we'll see what's possible."
      ]);
      return;
    }
    orderScreen = 'addr';
    orderEls.current = order;
    stopResendTimer();
    v.textContent = '';
    orderTitle(v);
    v.appendChild(el('h3', 'wf-chat-order-subtitle', 'Change my shipping address'));
    v.appendChild(el('p', 'wf-chat-order-copy',
      "Update where order " + (order.name || '') + " ships. Changes apply " +
      'right away, as long as the order has not shipped.'));

    var cur = order.shipping_address || {};
    var lockedCountry = (cur.country_code || 'US').toUpperCase();
    var countryName = lockedCountry;
    try {
      countryName = new Intl.DisplayNames(['en'], { type: 'region' }).of(lockedCountry) || lockedCountry;
    } catch (e) {}

    var form = el('form', 'wf-chat-form');
    form.setAttribute('novalidate', 'novalidate');
    var fName = field('Full name', 'wf-addr-name', 'text', 'Name');
    var f1 = field('Street address', 'wf-addr-1', 'text', '123 Main St');
    var f2 = field('Apt, suite, etc. (optional)', 'wf-addr-2', 'text', '');
    var fCity = field('City', 'wf-addr-city', 'text', '');
    fName.input.value = cur.name || '';
    f1.input.value = cur.address1 || '';
    f2.input.value = cur.address2 || '';
    fCity.input.value = cur.city || '';

    // State/province: dropdown for US/CA, free text for other countries.
    var regions = lockedCountry === 'US' ? US_STATES
      : (lockedCountry === 'CA' ? CA_PROVINCES : null);
    var stateWrap, getProvince;
    if (regions) {
      var sw = el('div', 'wf-chat-field');
      var sLabel = el('label', 'wf-chat-label', lockedCountry === 'US' ? 'State' : 'Province');
      sLabel.setAttribute('for', 'wf-addr-state');
      var sSel = el('select', 'wf-chat-select');
      sSel.id = 'wf-addr-state';
      var ph = document.createElement('option');
      ph.value = '';
      ph.textContent = lockedCountry === 'US' ? 'Select state' : 'Select province';
      sSel.appendChild(ph);
      regions.forEach(function (r) {
        var o = document.createElement('option');
        o.value = r[0];
        o.textContent = r[1];
        if ((cur.province_code || '').toUpperCase() === r[0]) o.selected = true;
        sSel.appendChild(o);
      });
      sw.appendChild(sLabel);
      sw.appendChild(sSel);
      stateWrap = sw;
      getProvince = function () { return sSel.value; };
    } else {
      var fState = field('State / province / region', 'wf-addr-state', 'text', '');
      fState.input.value = cur.province_code || '';
      stateWrap = fState.wrap;
      getProvince = function () { return fState.input.value.trim(); };
    }

    var fZip = field('ZIP / postal code', 'wf-addr-zip', 'text', '');
    fZip.input.value = cur.zip || '';

    // Country is locked to the checkout country — international changes
    // need a human (shipping cost + carrier differ).
    var cWrap = el('div', 'wf-chat-field');
    var cLabel = el('label', 'wf-chat-label', 'Country');
    cLabel.setAttribute('for', 'wf-addr-country');
    var cSel = el('select', 'wf-chat-select');
    cSel.id = 'wf-addr-country';
    cSel.disabled = true;
    var cOpt = document.createElement('option');
    cOpt.value = lockedCountry;
    cOpt.textContent = countryName;
    cOpt.selected = true;
    cSel.appendChild(cOpt);
    cWrap.appendChild(cLabel);
    cWrap.appendChild(cSel);

    form.appendChild(fName.wrap);
    form.appendChild(f1.wrap);
    form.appendChild(f2.wrap);
    form.appendChild(fCity.wrap);
    var row = el('div', 'wf-chat-addr-row');
    row.appendChild(stateWrap);
    row.appendChild(fZip.wrap);
    form.appendChild(row);
    form.appendChild(cWrap);

    var intlNote = el('p', 'wf-chat-addr-note');
    intlNote.appendChild(document.createTextNode(
      'Orders can only ship within ' + countryName + '. Need it shipped to another country? '));
    var intlLink = el('button', 'wf-chat-linkbtn', 'Chat with us');
    intlLink.type = 'button';
    intlLink.addEventListener('click', function () { helpChat(order); });
    intlNote.appendChild(intlLink);
    form.appendChild(intlNote);

    var err = el('div', 'wf-chat-error');
    err.setAttribute('role', 'alert');
    err.style.display = 'none';
    form.appendChild(err);
    var submit = primaryButton('Update address', true);
    form.appendChild(submit.btn);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      err.style.display = 'none';
      var address = {
        name: fName.input.value.trim(),
        address1: f1.input.value.trim(),
        address2: f2.input.value.trim(),
        city: fCity.input.value.trim(),
        province_code: getProvince(),
        zip: fZip.input.value.trim(),
        country_code: lockedCountry
      };
      if (!address.address1 || !address.city || !address.zip) {
        err.textContent = 'Street address, city, and ZIP are required.';
        err.style.display = 'block';
        return;
      }
      submit.setLoading(true);
      postJSON('/support/widget/order/address/', {
        session: sess.get('wf_chat_session'),
        order_ref: order.order_ref || '',
        address: address
      }).then(function (res) {
        submit.setLoading(false);
        if (res.ok && res.body && res.body.ok) {
          order.shipping_address = address;
          announce('Shipping address updated.');
          orderInfoScreen(v, order, 'addr', 'Address updated', [
            'Your order will now ship to:',
            res.body.address,
            "You're all set — no need to do anything else."
          ], 'Done — back to my order');
          // Repurpose the CTA: go back to the order instead of chat.
          var btn = v.querySelector('.wf-chat-btn--primary');
          if (btn) {
            var fresh = btn.cloneNode(true);
            btn.parentNode.replaceChild(fresh, btn);
            fresh.addEventListener('click', function () { renderOrderDetail(v, order); });
          }
        } else {
          err.textContent = (res.body && res.body.message) ||
            'We could not update the address — please try again.';
          err.style.display = 'block';
          announce(err.textContent);
        }
      }).catch(function () {
        submit.setLoading(false);
        err.textContent = 'Something went wrong. Please try again.';
        err.style.display = 'block';
      });
    });

    v.appendChild(form);
    setTimeout(function () { try { f1.input.focus(); } catch (e) {} }, 0);
  }

  // Status chip reusing the timeline status mapping.
  function statusChip(order) {
    var cls, label;
    if (order.cancelled) { cls = 'cancelled'; label = 'Cancelled'; }
    else if (order.fulfillment_status === 'fulfilled') { cls = 'shipped'; label = 'Shipped'; }
    else if (order.fulfillment_status === 'partial') { cls = 'partial'; label = 'Partially shipped'; }
    else { cls = 'production'; label = 'In production'; }
    return el('span', 'wf-chat-status-chip wf-chat-status-chip--' + cls, label);
  }

  // ---- (d) ORDER INFO screen (Richpanel style) ----------------------------
  // Compact order card over Summary (addresses) and Items (rows + money
  // breakdown). The shipment timeline lives on its own Track screen.
  function renderOrderDetail(v, order) {
    orderScreen = 'detail';
    orderEls.current = order;
    stopResendTimer();
    v.textContent = '';
    orderTitle(v, 'Order info');
    v.appendChild(orderCard(v, order, { mode: 'info' }));

    if (order.cancelled) {
      v.appendChild(el('p', 'wf-chat-order-cancelled', 'This order was cancelled.'));
    }

    // Summary: addresses, only when the backend sent them.
    var addrs = [
      ['Billing address', fmtAddress(order.billing_address)],
      ['Shipping address', fmtAddress(order.shipping_address)]
    ].filter(function (a) { return a[1] !== ''; });
    if (addrs.length) {
      var sum = el('div', 'wf-chat-order-panel');
      sum.appendChild(el('h4', 'wf-chat-order-panel__title', 'Summary'));
      addrs.forEach(function (a) {
        var blk = el('div', 'wf-chat-order-addr');
        blk.appendChild(el('p', 'wf-chat-order-addr__label', a[0]));
        blk.appendChild(el('p', 'wf-chat-order-addr__value', a[1]));
        sum.appendChild(blk);
      });
      v.appendChild(sum);
    }

    // Items + totals.
    var items = order.line_items || [];
    var panel = el('div', 'wf-chat-order-panel');
    panel.appendChild(el('h4', 'wf-chat-order-panel__title', 'Items'));
    if (items.length) {
      var lines = el('div', 'wf-chat-order-lines');
      items.forEach(function (it) {
        lines.appendChild(orderLineRow(it, order.currency));
      });
      panel.appendChild(lines);
    }
    var totals = el('div', 'wf-chat-order-totals');
    function totalsRow(label, value, strong) {
      var r = el('div', 'wf-chat-order-totals__row' + (strong ? ' wf-chat-order-totals__row--total' : ''));
      r.appendChild(el('span', 'wf-chat-order-totals__label', label));
      r.appendChild(el('span', 'wf-chat-order-totals__value', value));
      totals.appendChild(r);
    }
    if (order.subtotal_price != null) {
      totalsRow('Item(s) total', fmtMoney(order.subtotal_price, order.currency));
    }
    if (order.taxes != null) {
      totalsRow('Taxes', fmtMoney(order.taxes, order.currency));
    }
    if (order.shipping_charge != null) {
      totalsRow('Delivery charges', fmtMoney(order.shipping_charge, order.currency));
    }
    if (order.discounts != null && parseFloat(order.discounts) > 0) {
      totalsRow('Discounts', '−' + fmtMoney(order.discounts, order.currency));
    }
    if (order.total_price != null) {
      totalsRow('Total', fmtMoney(order.total_price, order.currency), true);
    }
    if (totals.childNodes.length) panel.appendChild(totals);
    v.appendChild(panel);

    setTimeout(function () {
      var h = v.querySelector('.wf-chat-section-title');
      if (h) { h.setAttribute('tabindex', '-1'); try { h.focus(); } catch (e) {} }
    }, 0);
  }

  // "11498 Torino Way, Frankfort, IL, 60423, US" — '' when nothing usable.
  function fmtAddress(a) {
    if (!a) return '';
    var parts = [a.address1, a.address2, a.city, a.province_code, a.zip, a.country_code];
    return parts.filter(function (p) { return p; }).join(', ');
  }

  // One Items row: thumb, title over "unit × qty", line total on the right.
  function orderLineRow(it, currency) {
    var row = el('div', 'wf-chat-order-line');
    if (it.image) {
      var img = el('img', 'wf-chat-order-line__thumb');
      img.src = thumbUrl(it.image, 88);
      img.alt = '';
      img.loading = 'lazy';
      img.width = 44;
      img.height = 44;
      row.appendChild(img);
    } else {
      row.appendChild(el('span', 'wf-chat-order-line__thumb'));
    }
    var col = el('span', 'wf-chat-order-line__col');
    col.appendChild(el('span', 'wf-chat-order-line__title', it.title || 'Item'));
    var qty = it.quantity || 1;
    var unit = parseFloat(it.price);
    if (!isNaN(unit)) {
      col.appendChild(el('span', 'wf-chat-order-line__sub',
        fmtMoney(it.price, currency) + ' × ' + qty));
    }
    row.appendChild(col);
    if (!isNaN(unit)) {
      row.appendChild(el('span', 'wf-chat-order-line__price',
        fmtMoney((unit * qty).toFixed(2), currency)));
    }
    return row;
  }

  // ---- (e) TRACK screen — the shipment timeline ---------------------------
  function renderOrderTrack(v, order) {
    orderScreen = 'track';
    orderEls.current = order;
    stopResendTimer();
    v.textContent = '';
    orderTitle(v, 'Track my order');
    var sub = el('p', 'wf-chat-signed-in', 'Order ' + (order.name || ''));
    v.appendChild(sub);

    if (order.cancelled) {
      v.appendChild(el('p', 'wf-chat-order-cancelled', 'This order was cancelled.'));
      if (order.financial_status) {
        v.appendChild(el('p', 'wf-chat-order-detail', 'Payment: ' + order.financial_status));
      }
    } else {
      v.appendChild(buildTimeline(order));

      // Expectation-setting copy driven by order type + age (numbers match
      // the live support agent: 5-10 bd custom production, 1-2 bd non-custom,
      // 3-5 bd standard transit).
      var notes = [];
      if (!orderShipped(order)) {
        if (isCustomOrder(order)) {
          notes.push('Personalized pieces are made to order and take 5–10 ' +
                     'business days before shipping.');
          if (order.order_type === 'mixed') {
            notes.push('Your order ships together once every piece is ready.');
          }
          if (inHighSeason()) {
            notes.push('During our busy season, please allow a few extra business days.');
          }
        } else if (order.order_type === 'noncustom') {
          notes.push('Non-personalized orders typically ship within 1–2 business days.');
        }
        var age = daysSince(order.created_at);
        if (age != null && age >= 17) {
          notes = ["This order is taking longer than expected — chat with us " +
                   "and we'll check on it right away."];
        }
      } else {
        var shipAge = daysSince(order.shipped_at || order.created_at);
        if (shipAge != null && shipAge >= 10) {
          notes.push("Still nothing after more than a week in transit? Chat with " +
                     "us and we'll look into it.");
        } else {
          notes.push('Standard shipping takes 3–5 business days once your ' +
                     'order is on its way.');
        }
      }
      notes.forEach(function (n) { v.appendChild(el('p', 'wf-chat-order-copy', n)); });
    }

    var chatBtn = el('button', 'wf-chat-btn wf-chat-btn--primary');
    chatBtn.type = 'button';
    chatBtn.textContent = ONLINE ? 'Chat about this order' : 'Message us about this order';
    chatBtn.addEventListener('click', function () { helpChat(order); });
    v.appendChild(chatBtn);

    setTimeout(function () {
      var h = v.querySelector('.wf-chat-section-title');
      if (h) { h.setAttribute('tabindex', '-1'); try { h.focus(); } catch (e) {} }
    }, 0);
  }

  // Vertical timeline derived from REAL order data.
  function buildTimeline(order) {
    var wrap = el('div', 'wf-chat-timeline');
    var ff = order.fulfillment_status;
    var shipped = ff === 'fulfilled' || ff === 'partial';
    var placedDetail = fmtDate(order.created_at);

    var steps = [];
    steps.push({
      label: 'Order placed', done: true,
      detail: placedDetail ? ('Placed ' + placedDetail) : 'Payment confirmed'
    });
    steps.push({
      label: 'In production', done: true,
      detail: isCustomOrder(order)
        ? 'Made to order — 5–10 business days'
        : (order.order_type === 'noncustom'
            ? 'Preparing your order — ships in 1–2 business days'
            : 'Your piece is being made')
    });

    // Shipped step: detail carries tracking links (safe) when present.
    var shippedStep = { label: 'Shipped', done: shipped, detailNodes: [] };
    if (shipped) {
      var tracking = order.tracking || [];
      if (tracking.length) {
        tracking.forEach(function (t, i) {
          if (i > 0) shippedStep.detailNodes.push(document.createElement('br'));
          if (t.url) {
            var a = el('a', 'wf-chat-track-link', t.number || 'Track package');
            a.href = t.url;
            a.target = '_blank';
            a.rel = 'noopener';
            a.appendChild(srSpan(' (opens in a new window)'));
            shippedStep.detailNodes.push(a);
          } else {
            shippedStep.detailNodes.push(document.createTextNode(t.number || 'Shipped'));
          }
        });
      } else {
        shippedStep.detailNodes.push(document.createTextNode('On its way to you'));
      }
    } else {
      shippedStep.detailNodes.push(document.createTextNode("We'll email tracking when it ships"));
    }
    steps.push(shippedStep);

    steps.push({
      label: 'Delivered', done: false,
      detail: shipped ? 'Estimated by carrier — see tracking' : '—'
    });

    steps.forEach(function (s, i) {
      var isLast = i === steps.length - 1;
      var rowEl = el('div', 'wf-chat-tl-row');

      var rail = el('div', 'wf-chat-tl-rail');
      var dot = el('span', 'wf-chat-tl-dot' + (s.done ? ' wf-chat-tl-dot--done' : ''));
      if (s.done) {
        dot.appendChild(svg(ICON.check, 'wf-chat-tl-check', { strokeWidth: 2.4 }));
        dot.appendChild(srSpan('done'));
      }
      rail.appendChild(dot);
      if (!isLast) rail.appendChild(el('span', 'wf-chat-tl-line'));
      rowEl.appendChild(rail);

      var body = el('div', 'wf-chat-tl-body');
      body.appendChild(el('p', 'wf-chat-tl-label' + (s.done ? '' : ' wf-chat-tl-label--pending'), s.label));
      var detail = el('p', 'wf-chat-tl-detail');
      if (s.detailNodes) {
        s.detailNodes.forEach(function (n) { detail.appendChild(n); });
      } else {
        detail.textContent = s.detail;
      }
      body.appendChild(detail);
      rowEl.appendChild(body);

      wrap.appendChild(rowEl);
    });

    return wrap;
  }

  /*==========================================================================
    Shared form field + button builders
  ==========================================================================*/
  function field(labelText, id, type, placeholder) {
    var wrap = el('div', 'wf-chat-field');
    var label = el('label', 'wf-chat-label', labelText);
    label.setAttribute('for', id);
    var input = el('input', 'wf-chat-input');
    input.type = type || 'text';
    input.id = id;
    if (placeholder) input.placeholder = placeholder;
    wrap.appendChild(label);
    wrap.appendChild(input);
    return { wrap: wrap, input: input, label: label };
  }

  function textareaField(labelText, id, placeholder, rows) {
    var wrap = el('div', 'wf-chat-field');
    var label = el('label', 'wf-chat-label', labelText);
    label.setAttribute('for', id);
    var ta = el('textarea', 'wf-chat-textarea');
    ta.id = id;
    ta.rows = rows || 4;
    if (placeholder) ta.placeholder = placeholder;
    wrap.appendChild(label);
    wrap.appendChild(ta);
    return { wrap: wrap, input: ta, label: label };
  }

  // Primary button with optional inline spinner; returns { btn, setLoading }.
  function primaryButton(text, withSpinner) {
    var btn = el('button', 'wf-chat-btn wf-chat-btn--primary');
    btn.type = 'submit';
    var spinner = null;
    if (withSpinner) {
      spinner = el('span', 'wf-chat-btn__spinner');
      spinner.setAttribute('aria-hidden', 'true');
      btn.appendChild(spinner);
    }
    btn.appendChild(document.createTextNode(text));
    return {
      btn: btn,
      setLoading: function (on) {
        btn.disabled = on;
        btn.classList.toggle('wf-chat-btn--loading', on);
      }
    };
  }

  /*==========================================================================
    OFFLINE / contact view
  ==========================================================================*/
  function buildOfflineView() {
    var v = makeView('offline', false);
    v._reset = function () {
      v.textContent = '';
      v.appendChild(el('h3', 'wf-chat-section-title', 'Leave us a message'));
      if (state.config.offline_note) {
        v.appendChild(el('p', 'wf-chat-intro', state.config.offline_note));
      }
      var form = el('form', 'wf-chat-form');
      form.setAttribute('novalidate', 'novalidate');

      var nameField = field('Name (optional)', 'wf-chat-off-name', 'text', 'Your name');
      if (CUSTOMER_NAME) nameField.input.value = CUSTOMER_NAME;
      var emailField = field('Email', 'wf-chat-off-email', 'email', 'you@email.com');
      if (CUSTOMER_EMAIL) emailField.input.value = CUSTOMER_EMAIL;
      var msgField = textareaField('Message', 'wf-chat-off-msg', 'How can we help?', 4);

      var err = el('div', 'wf-chat-error');
      err.setAttribute('role', 'alert');
      err.style.display = 'none';

      form.appendChild(nameField.wrap);
      form.appendChild(emailField.wrap);
      form.appendChild(msgField.wrap);
      form.appendChild(err);

      var submit = primaryButton('Send message', true);
      form.appendChild(submit.btn);

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        err.style.display = 'none';
        var email = emailField.input.value.trim();
        var body = msgField.input.value.trim();
        if (!email || !body) {
          err.textContent = 'Please add your email and a message.';
          err.style.display = 'block';
          return;
        }
        submit.setLoading(true);
        var payload = { name: nameField.input.value.trim(), email: email, message: body };
        if (state.orderRef) payload.order_ref = state.orderRef;
        // Consumed once — don't tag a later, unrelated message with this order.
        state.orderRef = null;
        postJSON('/support/widget/offline-message/', payload).then(function (res) {
          submit.setLoading(false);
          if (res.ok && res.body && res.body.ok) {
            if (res.body.token) store.set('wf_chat_token', res.body.token);
            renderContactSuccess(v);
            announce("Message sent. We'll reply by email soon.");
          } else {
            var m = (res.body && res.body.message) || 'Could not send your message.';
            err.textContent = m;
            err.style.display = 'block';
            announce(m);
          }
        }).catch(function () {
          submit.setLoading(false);
          err.textContent = 'Something went wrong. Please try again.';
          err.style.display = 'block';
        });
      });

      v.appendChild(form);
    };
  }

  function renderContactSuccess(v) {
    v.textContent = '';
    var box = el('div', 'wf-chat-success');
    var ring = el('span', 'wf-chat-success__ring');
    ring.appendChild(svg(ICON.check, null, { strokeWidth: 1.8 }));
    box.appendChild(ring);
    box.appendChild(el('p', 'wf-chat-success__title', 'Message sent'));
    box.appendChild(el('p', 'wf-chat-success__body',
      "Thank you — we'll reply to your email within one business day."));
    var back = el('button', 'wf-chat-btn wf-chat-btn--secondary');
    back.type = 'button';
    back.textContent = 'Back to home';
    back.addEventListener('click', function () { setView('home'); });
    box.appendChild(back);
    v.appendChild(box);
  }

  /*==========================================================================
    CHAT view
  ==========================================================================*/
  var chatEls = {};

  function buildChatView() {
    var v = makeView('chat', false);
    chatEls.view = v;
    v._enter = enterChat;
  }

  function enterChat() {
    var v = chatEls.view;
    v.textContent = '';

    // No pre-chat form: visitors can type straight away. Identity, when we
    // have one, rides along with the first message — Shopify-logged-in email,
    // then an OTP-verified email (only set when the user verified with an
    // email, not a phone), then one given in an earlier chat. Otherwise the
    // thread asks for an email after the first message (email card).
    var verifiedEmail = sess.get('wf_chat_session') ? sess.get('wf_chat_email_verified') : '';
    chatEls.email = CUSTOMER_EMAIL || verifiedEmail || store.get('wf_chat_email') || '';
    chatEls.name = CUSTOMER_NAME || store.get('wf_chat_name') || '';
    chatEls.pendingNoEmail = [];
    chatEls.emailRequired = false;
    renderThread(v);
    // Reaching the chat view means the user is now looking at the messages —
    // drop any unread badge raised while they were on another view.
    clearUnread();
    connect();
  }

  function renderThread(v) {
    // Re-arm the REST/WS reconciliation guard for this fresh render. If a WS
    // 'session' frame rebuilds the thread while the REST history fetch is in
    // flight, the flag flips back to true and the stale REST result is dropped.
    state.sessionRebuilt = false;
    state.hasCustomerMsg = false;

    var thread = el('div', 'wf-chat-thread');
    chatEls.emailCard = null;      // belonged to the previous thread, if any
    chatEls.rebuildAnchor = null;
    thread.setAttribute('role', 'log');
    thread.setAttribute('aria-live', 'polite');
    thread.setAttribute('aria-label', 'Conversation');
    chatEls.thread = thread;

    // Who you're talking to. Static furniture like the day separator —
    // clearThread keeps it when history is rebuilt.
    var intro = el('div', 'wf-chat-thread-intro');
    intro.appendChild(avatarBadge());
    intro.appendChild(el('p', 'wf-chat-thread-intro__name', 'Wear Felicity team'));
    intro.appendChild(presenceLine());
    thread.appendChild(intro);

    // "TODAY" separator
    thread.appendChild(el('div', 'wf-chat-day', 'Today'));

    var typing = el('div', 'wf-chat-typing');
    typing.appendChild(el('span'));
    typing.appendChild(el('span'));
    typing.appendChild(el('span'));
    typing.appendChild(srSpan('Support is typing'));
    chatEls.typing = typing;
    thread.appendChild(typing);

    // Resuming an existing conversation: its history is about to load, so the
    // empty-thread furniture (greeting bubble) must not flash first.
    // rebuildThread restores it if history turns out empty.
    var token = store.get('wf_chat_token');

    // Borderless composer
    var composer = el('form', 'wf-chat-composer');
    var taLabel = el('label', 'wf-chat-sr', 'Type your message');
    taLabel.setAttribute('for', 'wf-chat-composer-input');
    var ta = el('textarea', 'wf-chat-composer__input');
    ta.id = 'wf-chat-composer-input';
    ta.rows = 1;
    ta.placeholder = 'Write a message…';
    chatEls.input = ta;
    var send = iconButton('wf-chat-composer__send', 'Send message',
      svg(ICON.send, null, { strokeWidth: 1.7 }), 'Send message');
    send.type = 'submit';
    chatEls.send = send;

    ta.addEventListener('input', function () {
      // Reset to the CSS 44px baseline, then grow only when content wraps
      // (inline 'auto' would let the theme's global textarea height bleed in)
      ta.style.height = '';
      if (ta.scrollHeight > ta.clientHeight) {
        ta.style.height = Math.min(ta.scrollHeight, 96) + 'px';
      }
      // Scrollbar only once the textarea is pinned at its max height
      ta.style.overflowY = ta.scrollHeight > 96 ? 'auto' : 'hidden';
      sendTyping(true);
    });
    ta.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        composer.requestSubmit ? composer.requestSubmit() : sendCurrent();
      }
    });
    composer.addEventListener('submit', function (e) { e.preventDefault(); sendCurrent(); });

    composer.appendChild(taLabel);
    composer.appendChild(ta);
    composer.appendChild(send);

    v.appendChild(thread);
    v.appendChild(composer);

    // Local, non-persisted greeting bubble when the conversation is empty.
    // NOT sent to the server and NOT added to seenMsgIds. A resumed
    // conversation shows shimmer message skeletons until history arrives.
    chatEls.greetingShown = false;
    if (!token) maybeShowGreeting();
    else showThreadSkeleton();

    // Restore history before the socket opens. If the WS 'session' frame has
    // already rebuilt the thread by the time this resolves, discard the (now
    // stale) REST result — otherwise the slower response would clobber newer
    // state (including any messages that arrived after connect).
    if (token) {
      getJSON('/support/widget/conversation/?token=' + encodeURIComponent(token)).then(function (res) {
        if (state.sessionRebuilt) return;
        removeThreadSkeleton();
        if (res.ok && res.body && res.body.messages) {
          rebuildThread(res.body.messages);
          if (res.body.status === 'resolved') showResolved();
        } else if (res.status === 404) {
          // Dead token — this is a fresh thread after all: restore the
          // empty-conversation furniture the skeleton was standing in for.
          store.del('wf_chat_token');
          maybeShowGreeting();
          if (chatEls.chips) chatEls.chips.style.display = '';
        }
      }).catch(function () { removeThreadSkeleton(); });
    }

    setTimeout(function () { try { ta.focus(); } catch (e) {} }, 0);
  }

  // Show a local greeting bubble only when the thread has no real messages.
  function maybeShowGreeting() {
    var thread = chatEls.thread;
    if (!thread || chatEls.greetingShown) return;
    if (threadHasMessages()) return;
    chatEls.greetingShown = true;
    var wrap = el('div', 'wf-chat-msg wf-chat-msg--in wf-chat-msg--greeting');
    var bubble = el('div', 'wf-chat-msg__bubble',
      'Hi! Ask us anything about our jewelry, engraving, or your order.');
    wrap.appendChild(bubble);
    if (chatEls.typing && chatEls.typing.parentNode === thread) {
      thread.insertBefore(wrap, chatEls.typing);
    } else {
      thread.appendChild(wrap);
    }
    chatEls.greetingNode = wrap;
  }

  /*--------------------------------------------------------------------------
    Email card — asked AFTER the first message, not before it. Chats can
    start anonymously; the address lets the backend email replies the
    visitor misses after leaving (support/services/chat_email.py).
    `required` is the fallback for a backend that still insists on an email
    before creating the conversation (error 'email_required').
  --------------------------------------------------------------------------*/
  function maybeShowEmailCard(required) {
    var thread = chatEls.thread;
    if (!thread || chatEls.email) return;
    if (!required && !state.hasCustomerMsg) return;
    if (chatEls.emailCard) {
      if (required) chatEls.emailCard._setRequired();
      return;
    }
    var card = el('form', 'wf-chat-emailcard');
    card.setAttribute('novalidate', 'novalidate');
    card.setAttribute('aria-label', 'Your email');
    var title = el('p', 'wf-chat-emailcard__title');
    var sub = el('p', 'wf-chat-emailcard__sub');
    card._setRequired = function () {
      title.textContent = 'Add your email to send your message';
      sub.textContent = 'We need it to start the chat, and to reply if you step away.';
    };
    title.textContent = 'Get replies by email';
    sub.textContent = 'So we can reach you if you step away.';
    if (required) card._setRequired();
    card.appendChild(title);
    card.appendChild(sub);

    var row = el('div', 'wf-chat-emailcard__row');
    var label = el('label', 'wf-chat-sr', 'Email address');
    label.setAttribute('for', 'wf-chat-email-ask');
    var input = el('input', 'wf-chat-input wf-chat-emailcard__input');
    input.type = 'email';
    input.id = 'wf-chat-email-ask';
    input.placeholder = 'you@email.com';
    input.autocomplete = 'email';
    var save = el('button', 'wf-chat-btn wf-chat-btn--primary wf-chat-emailcard__btn', 'Save');
    save.type = 'submit';
    row.appendChild(label);
    row.appendChild(input);
    row.appendChild(save);
    card.appendChild(row);
    var err = el('p', 'wf-chat-emailcard__err');
    err.id = 'wf-chat-email-ask-err';
    err.setAttribute('role', 'alert');
    err.hidden = true;
    card.appendChild(err);

    card.addEventListener('submit', function (e) {
      e.preventDefault();
      var value = input.value.trim();
      if (!isEmail(value)) {
        err.textContent = 'Enter a valid email address.';
        err.hidden = false;
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', err.id);
        try { input.focus(); } catch (x) {}
        return;
      }
      submitVisitorEmail(value);
    });

    insertIntoThread(card);
    chatEls.emailCard = card;
    thread.scrollTop = thread.scrollHeight;
  }

  function submitVisitorEmail(email) {
    store.set('wf_chat_email', email);
    chatEls.email = email;
    var pending = chatEls.pendingNoEmail || [];
    chatEls.pendingNoEmail = [];
    if (chatEls.emailRequired && pending.length) {
      // Older backend rejected these for lack of an email — resend with it.
      // (Only after an actual rejection: otherwise they may merely be
      // unconfirmed, and re-sending would duplicate them.)
      chatEls.emailRequired = false;
      pending.forEach(function (payload) {
        payload.customer_email = email;
        queueMessage(payload);
      });
    } else {
      queueMessage({ type: 'identify', email: email, name: chatEls.name || '' });
    }
    var card = chatEls.emailCard;
    chatEls.emailCard = null;
    if (card && card.parentNode) {
      var done = el('div', 'wf-chat-msg wf-chat-msg--system wf-chat-emailcard-done',
        'Thanks! If you step away, we’ll email our reply to ' + email + '.');
      card.parentNode.replaceChild(done, card);
    }
    try { chatEls.input.focus(); } catch (x) {}
  }

  function removeGreeting() {
    if (chatEls.greetingNode && chatEls.greetingNode.parentNode) {
      chatEls.greetingNode.parentNode.removeChild(chatEls.greetingNode);
    }
    chatEls.greetingNode = null;
  }

  // Three shimmer bubbles standing in for the conversation being fetched.
  function showThreadSkeleton() {
    var thread = chatEls.thread;
    if (!thread || chatEls.threadSkel) return;
    var wrap = el('div', 'wf-chat-thread-skel');
    var g = skelGroup(wrap, 'Loading conversation…', 'wf-chat-thread-skel__group');
    [['in', '58%'], ['out', '44%'], ['in', '70%']].forEach(function (m) {
      g.appendChild(skelBar('wf-chat-skel--bubble wf-chat-skel--bubble-' + m[0], m[1]));
    });
    if (chatEls.typing && chatEls.typing.parentNode === thread) {
      thread.insertBefore(wrap, chatEls.typing);
    } else {
      thread.appendChild(wrap);
    }
    chatEls.threadSkel = wrap;
  }

  function removeThreadSkeleton() {
    if (chatEls.threadSkel && chatEls.threadSkel.parentNode) {
      chatEls.threadSkel.parentNode.removeChild(chatEls.threadSkel);
    }
    chatEls.threadSkel = null;
  }

  function threadHasMessages() {
    var thread = chatEls.thread;
    if (!thread) return false;
    return !!thread.querySelector('.wf-chat-msg:not(.wf-chat-msg--greeting)');
  }

  function hideChips() {
    if (chatEls.chips) chatEls.chips.style.display = 'none';
  }

  function sendCurrent() {
    var ta = chatEls.input;
    if (!ta) return;
    var text = ta.value.trim();
    if (!text) return;
    ta.value = '';
    ta.style.height = '';
    sendText(text);
  }

  function sendText(text) {
    text = (text || '').trim();
    if (!text) return;
    removeGreeting();
    removeThreadSkeleton();
    // Optimistic render (server does NOT echo our own messages).
    appendMessage('out', text, null, new Date().toISOString());
    state.hasCustomerMsg = true;
    hideChips();
    sendTyping(false);

    var payload = { type: 'message', content: text };
    if (!store.get('wf_chat_token')) {
      payload.customer_email = chatEls.email || '';
      payload.customer_name = chatEls.name || '';
      if (state.orderRef) payload.order_ref = state.orderRef;
      // Held until the server confirms the conversation ('session' frame):
      // an older backend that still requires an email rejects these with
      // 'email_required', and they're re-sent once the visitor gives one.
      if (!chatEls.email && chatEls.pendingNoEmail) chatEls.pendingNoEmail.push(payload);
    }
    // The order_ref belongs to the first message of this conversation only;
    // clear it so later, unrelated messages aren't tagged with a stale order.
    state.orderRef = null;
    queueMessage(payload);
    maybeShowEmailCard(false);
  }

  // Send now if the socket is OPEN, otherwise buffer and flush on 'open' so a
  // typed message is never silently dropped during connect/reconnect gaps.
  function queueMessage(payload) {
    if (ws && ws.readyState === WebSocket.OPEN) {
      wsSend(payload);
      return;
    }
    outbox.push(payload);
    connect();
    // If we're still not connected after a moment, tell the user we're trying.
    if (!outboxNoticeTimer) {
      outboxNoticeTimer = setTimeout(function () {
        outboxNoticeTimer = null;
        if (outbox.length && (!ws || ws.readyState !== WebSocket.OPEN)) {
          appendSystem('Connecting…');
        }
      }, 2000);
    }
  }

  function flushOutbox() {
    if (outboxNoticeTimer) { clearTimeout(outboxNoticeTimer); outboxNoticeTimer = null; }
    if (!outbox.length) return;
    var pending = outbox;
    outbox = [];
    pending.forEach(function (payload) { wsSend(payload); });
  }

  /*==========================================================================
    Message rendering
  ==========================================================================*/
  function appendMessage(dir, content, senderLabel, iso) {
    var thread = chatEls.thread;
    if (!thread) return;
    var wrap = el('div', 'wf-chat-msg wf-chat-msg--' + (dir === 'out' ? 'out' : 'in'));
    var bubble = el('div', 'wf-chat-msg__bubble');
    bubble.textContent = content;
    wrap.appendChild(bubble);
    var metaBits = [];
    if (senderLabel) metaBits.push(senderLabel);
    var t = fmtTime(iso);
    if (t) metaBits.push(t);
    if (metaBits.length) wrap.appendChild(el('div', 'wf-chat-msg__meta', metaBits.join(' · ')));
    insertIntoThread(wrap);
    thread.scrollTop = thread.scrollHeight;
    return wrap;
  }

  // New thread nodes go before the typing indicator so it stays last —
  // or, while a history rebuild is running, before the preserved email card
  // (see rebuildThread) so that card never has to move.
  function insertIntoThread(node) {
    var thread = chatEls.thread;
    var anchor = chatEls.rebuildAnchor || chatEls.typing;
    if (anchor && anchor.parentNode === thread) thread.insertBefore(node, anchor);
    else thread.appendChild(node);
  }

  function appendSystem(text) {
    var thread = chatEls.thread;
    if (!thread) return;
    var wrap = el('div', 'wf-chat-msg wf-chat-msg--system', text);
    insertIntoThread(wrap);
    thread.scrollTop = thread.scrollHeight;
  }

  function clearThread() {
    // Rebuild-from-history support: drop every rendered message/system/CSAT
    // node, keeping only the "Today" separator, typing indicator, team intro
    // and an unanswered email card. History is the source of truth after a
    // (re)connect — optimistic and live-rendered bubbles are replaced
    // wholesale so reconnects can never duplicate. The email card is KEPT
    // (not re-created) so an address the visitor is mid-way through typing —
    // and their focus/keyboard — survives the reconnect.
    var thread = chatEls.thread;
    if (!thread) return;
    removeThreadSkeleton();
    Array.prototype.slice.call(thread.children).forEach(function (node) {
      if (node !== chatEls.typing && node !== chatEls.emailCard &&
          !node.classList.contains('wf-chat-day') &&
          !node.classList.contains('wf-chat-thread-intro')) {
        thread.removeChild(node);
      }
    });
    state.seenMsgIds = {};
    chatEls.resolvedShown = false;
    chatEls.greetingShown = false;
    chatEls.greetingNode = null;
  }

  // Clear + repopulate the thread from an authoritative history array WITHOUT
  // re-announcing the whole transcript: role="log" carries an implicit
  // aria-live, so we silence it during the bulk rebuild and restore polite
  // announcements afterward (for genuinely new, live messages).
  function rebuildThread(messages) {
    var thread = chatEls.thread;
    if (!thread) return;
    thread.setAttribute('aria-live', 'off');
    clearThread();
    // History lands above a preserved email card (it stays the newest item).
    chatEls.rebuildAnchor = chatEls.emailCard && chatEls.emailCard.parentNode === thread
      ? chatEls.emailCard : null;
    messages.forEach(function (m) { appendServerMessage(m, true); });
    chatEls.rebuildAnchor = null;
    // If history turned out empty, still offer the local greeting + chips
    // (both are suppressed during a resume render until history arrives).
    maybeShowGreeting();
    maybeShowEmailCard(false);   // resumed an email-less thread on this device
    if (!state.hasCustomerMsg && chatEls.chips) chatEls.chips.style.display = '';
    setTimeout(function () {
      if (chatEls.thread === thread) thread.setAttribute('aria-live', 'polite');
    }, 0);
  }

  function appendServerMessage(m, isHistory) {
    // If the chat view isn't rendered (user is on home/help/order), there's no
    // thread to draw into. Do NOT mark the id seen — just flag unread. When the
    // user enters the chat view, history is re-fetched and the message appears.
    if (!chatEls.thread) {
      if (!isHistory) markUnread();
      return;
    }
    if (m.id != null) {
      if (state.seenMsgIds[m.id]) return;
      state.seenMsgIds[m.id] = true;
    }
    var isCustomer = m.sender_type === 'customer' || m.direction === 'out';
    if (isCustomer) {
      removeGreeting();
      state.hasCustomerMsg = true;
      hideChips();
      appendMessage('out', m.content || '', null, m.timestamp);
    } else {
      var label = m.sender_type === 'agent' ? 'Wear Felicity'
        : (m.sender_type === 'ai' || m.sender_type === 'bot') ? 'Wear Felicity' : null;
      appendMessage('in', m.content || '', label, m.timestamp);
    }
    if (!isHistory && !state.open) markUnread();
  }

  /*==========================================================================
    CSAT
  ==========================================================================*/
  function showResolved() {
    if (chatEls.resolvedShown) return;
    chatEls.resolvedShown = true;
    appendSystem('Conversation ended');
    renderCsat();
  }

  function renderCsat() {
    if (state.csatDone || !chatEls.thread) return;
    var wrap = el('div', 'wf-chat-csat');
    wrap.appendChild(el('p', 'wf-chat-csat__q', 'How did we do?'));
    // Plain buttons in a role="group" (NOT a radiogroup — that would require
    // arrow-key navigation + roving tabindex we don't implement). Each star is
    // individually Tab-reachable and Enter/Space-activatable; aria-pressed
    // reflects whether it's part of the current rating.
    var stars = el('div', 'wf-chat-stars');
    stars.setAttribute('role', 'group');
    stars.setAttribute('aria-label', 'Rate your support experience');
    var chosen = 0;
    var starBtns = [];
    for (var i = 1; i <= 5; i++) {
      (function (score) {
        var b = el('button', 'wf-chat-star');
        b.type = 'button';
        b.setAttribute('aria-pressed', 'false');
        b.setAttribute('aria-label', 'Rate ' + score + ' of 5');
        b.appendChild(svg(ICON.star, null, { strokeWidth: 1.6 }));
        b.addEventListener('click', function () {
          chosen = score;
          starBtns.forEach(function (sb, idx) {
            var on = idx < score;
            sb.classList.toggle('wf-chat-star--on', on);
            sb.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
        });
        starBtns.push(b);
        stars.appendChild(b);
      })(i);
    }
    wrap.appendChild(stars);

    var commentField = textareaField('Optional comment', 'wf-chat-csat-comment',
      'Anything to add? (optional)', 2);
    commentField.label.className = 'wf-chat-sr';   // keep label for SR, hide visually
    wrap.appendChild(commentField.wrap);
    var comment = commentField.input;

    var err = el('div', 'wf-chat-error');
    err.setAttribute('role', 'alert');
    err.style.display = 'none';
    wrap.appendChild(err);

    var submit = el('button', 'wf-chat-btn wf-chat-btn--primary');
    submit.type = 'button';
    submit.textContent = 'Send rating';
    submit.addEventListener('click', function () {
      if (!chosen) {
        err.textContent = 'Please pick a star rating first.';
        err.style.display = 'block';
        return;
      }
      var token = store.get('wf_chat_token');
      submit.disabled = true;
      err.style.display = 'none';
      postJSON('/support/widget/csat/', {
        token: token, score: chosen, comment: comment.value.trim()
      }).then(function (res) {
        if (res.ok && res.body && res.body.ok !== false) {
          state.csatDone = true;
          wrap.textContent = '';
          wrap.appendChild(el('p', 'wf-chat-csat__q', 'Thanks for the feedback!'));
          announce('Thanks for the feedback.');
        } else {
          // 400/404/etc: keep the form so the rating isn't silently lost.
          submit.disabled = false;
          err.textContent = (res.body && res.body.message) || 'Could not send your rating.';
          err.style.display = 'block';
        }
      }).catch(function () {
        submit.disabled = false;
        err.textContent = 'Could not send your rating.';
        err.style.display = 'block';
      });
    });
    wrap.appendChild(submit);

    var newChat = el('button', 'wf-chat-btn wf-chat-btn--secondary');
    newChat.type = 'button';
    newChat.textContent = 'Start a new chat';
    newChat.style.marginTop = '8px';
    newChat.addEventListener('click', function () {
      startNewConversation();
      enterChat();
    });
    wrap.appendChild(newChat);

    chatEls.thread.appendChild(wrap);
    chatEls.thread.scrollTop = chatEls.thread.scrollHeight;
  }

  /*==========================================================================
    WebSocket
  ==========================================================================*/
  function wsUrl() {
    var base = BACKEND.replace(/^http/, 'ws');
    return base + '/ws/support/chat/';
  }

  function connect() {
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
    try {
      ws = new WebSocket(wsUrl());
    } catch (e) {
      scheduleReconnect();
      return;
    }
    ws.addEventListener('open', function () {
      wsReconnectAttempts = 0;
      var token = store.get('wf_chat_token');
      if (token) wsSend({ type: 'init', token: token });
      startPing();
      if (chatEls.connLost) { chatEls.connLost = false; }
      // Flush anything typed while the socket was down (order preserved).
      flushOutbox();
    });
    ws.addEventListener('message', function (ev) {
      var data;
      try { data = JSON.parse(ev.data); } catch (e) { return; }
      handleWsMessage(data);
    });
    ws.addEventListener('close', function () {
      stopPing();
      if (state.open || store.get('wf_chat_token')) scheduleReconnect();
    });
    ws.addEventListener('error', function () { try { ws.close(); } catch (e) {} });
  }

  function handleWsMessage(data) {
    switch (data.type) {
      case 'session':
        if (data.token) {
          store.set('wf_chat_token', data.token);
          // Conversation exists server-side — nothing left to re-send.
          chatEls.pendingNoEmail = [];
        } else if (data.resumed === false) {
          // Token was dead and no replacement issued: drop it so the next
          // message starts a fresh conversation.
          store.del('wf_chat_token');
        }
        if (data.messages && data.messages.length) {
          // Replace, don't append: on reconnect the same history comes down
          // again and live/optimistic bubbles are already on screen.
          state.sessionRebuilt = true;
          rebuildThread(data.messages);
        }
        if (data.status === 'resolved') showResolved();
        break;
      case 'message':
        appendServerMessage({
          id: data.message_id,
          sender_type: data.sender_type,
          content: data.content,
          timestamp: data.timestamp
        }, false);
        break;
      case 'typing':
        setTypingIndicator(!!data.is_typing);
        break;
      case 'status':
        if (data.status === 'resolved') showResolved();
        break;
      case 'system':
        if (data.event === 'agent_joined') appendSystem('An agent has joined the chat.');
        break;
      case 'error':
        if (data.code === 'email_required') {
          // Backend predates email-optional chats: ask now, re-send after.
          if (chatEls.email) { store.del('wf_chat_email'); chatEls.email = ''; }
          chatEls.emailRequired = true;
          maybeShowEmailCard(true);
          break;
        }
        if (data.code === 'invalid_email') {
          // Server disagreed with our client-side check — ask again.
          store.del('wf_chat_email');
          chatEls.email = '';
          maybeShowEmailCard(false);
        }
        if (data.message) appendSystem(data.message);
        break;
      case 'identified':
        break;
      case 'pong':
        break;
    }
  }

  function setTypingIndicator(on) {
    if (chatEls.typing) chatEls.typing.classList.toggle('wf-chat-typing--on', on);
    if (chatEls.thread && on) chatEls.thread.scrollTop = chatEls.thread.scrollHeight;
  }

  function wsSend(obj) {
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify(obj)); } catch (e) {}
    }
  }

  function sendTyping(isTyping) {
    var now = Date.now();
    if (isTyping && now - typingSentAt < 2000) return;
    typingSentAt = now;
    wsSend({ type: 'typing', is_typing: !!isTyping });
  }

  function startPing() {
    stopPing();
    pingTimer = setInterval(function () { wsSend({ type: 'ping' }); }, 45000);
  }
  function stopPing() { if (pingTimer) { clearInterval(pingTimer); pingTimer = null; } }

  function scheduleReconnect() {
    if (wsReconnectTimer) return;
    if (!state.open && !store.get('wf_chat_token')) return;
    var delay = Math.min(30000, 1000 * Math.pow(2, wsReconnectAttempts));
    wsReconnectAttempts++;
    if (wsReconnectAttempts >= 2 && !chatEls.connLost && chatEls.thread) {
      chatEls.connLost = true;
      appendSystem('Connection lost — retrying…');
    }
    wsReconnectTimer = setTimeout(function () {
      wsReconnectTimer = null;
      connect();
    }, delay);
  }

  function closeSocket() {
    stopPing();
    if (wsReconnectTimer) { clearTimeout(wsReconnectTimer); wsReconnectTimer = null; }
    if (ws) { try { ws.close(); } catch (e) {} ws = null; }
  }

  /*==========================================================================
    Unread badge
  ==========================================================================*/
  function markUnread() {
    if (!launcher) return;
    launcher.classList.add('wf-chat-launcher--unread');
    // Panel is closed here, so its internal live region is display:none —
    // announce through the persistent external region instead.
    announceExternal('New message from support');
  }
  function clearUnread() {
    if (launcher) launcher.classList.remove('wf-chat-launcher--unread');
  }

  /*==========================================================================
    View / navigation
  ==========================================================================*/
  var viewHistory = [];     // where the header back arrow returns to
  var VIEW_TITLES = { help: 'Help', order: 'Your orders', chat: 'Wear Felicity', offline: 'Wear Felicity' };

  function setView(name, arg, opts) {
    if (state.view && state.view !== name && !(opts && opts.fromBack)) {
      viewHistory.push(state.view);
      if (viewHistory.length > 12) viewHistory.shift();
    }
    state.view = name;
    Object.keys(views).forEach(function (k) {
      views[k].classList.toggle('wf-chat-view--active', k === name);
    });
    var isHome = name === 'home';
    // Returning home ends the "arrived from an order" context — a subsequent
    // chat/offline message must not carry a stale order_ref.
    if (isHome) state.orderRef = null;

    // The home view has its own black hero header; every other view shows the
    // chrome header. Back button only on non-home views.
    header.style.display = isHome ? 'none' : 'flex';
    backBtn.style.display = isHome ? 'none' : 'flex';
    panel.classList.toggle('wf-chat-panel--home', isHome);
    // The header names where you are; the online status only matters where
    // you'd be talking to the team (chat / leave a message).
    headerBrand.textContent = VIEW_TITLES[name] || 'Wear Felicity';
    headerStatus.style.display = (name === 'chat' || name === 'offline') ? '' : 'none';
    // Home sits on ivory and Help on the warm canvas so their white cards separate.
    bodyEl.classList.toggle('wf-chat-body--ivory', isHome);
    bodyEl.classList.toggle('wf-chat-body--canvas', name === 'help');

    // Per-view (re)build hooks.
    if (name === 'order' && views.order._reset) views.order._reset();
    if (name === 'offline' && views.offline._reset) views.offline._reset();
    if (name === 'help' && views.help._enter) {
      views.help._enter(arg || null, !!(opts && opts.fromBack));
    }
    if (name === 'chat' && chatEls.view._enter) chatEls.view._enter();
    if (isHome) refreshHomeExtras();   // refresh convos card + logout row

    updateTabs(name);

    // Move focus to the first focusable in the new view for keyboard users.
    // Skipped during the initial open (open() focuses the dialog itself).
    if (state.open && !opening) setTimeout(function () { focusFirst(bodyEl); }, 0);
  }

  function goBack() {
    // Inside the order/help flows, back first walks the flow's own screens.
    if (state.view === 'order' && views.order._back && views.order._back()) return;
    if (state.view === 'help' && views.help._back && views.help._back()) return;
    var prev = viewHistory.pop();
    if (prev) setView(prev, null, { fromBack: true });
    else setView('home');
  }

  /*==========================================================================
    Open / close + focus management
  ==========================================================================*/
  function toggle() { state.open ? close() : open(); }

  function open() {
    if (state.open) return;
    state.lastFocus = document.activeElement;
    state.open = true;
    if (heroLogoImg && !heroLogoImg.src) heroLogoImg.src = LOGO_URL;
    panel.classList.add('wf-chat-panel--open');
    launcher.setAttribute('aria-expanded', 'true');
    launcher.querySelector('.wf-chat-sr').textContent = 'Close support chat';
    clearUnread();
    if (window.innerWidth < 590) lockBodyScroll(true);
    // Suppress setView's focus-into-view during the open so we can move focus
    // to the dialog itself (not a control) — avoids a control ring on a pointer
    // open, especially the close X on the mobile takeover.
    opening = true;
    setView(state.view || 'home');
    opening = false;
    setTimeout(function () { try { panel.focus(); } catch (e) {} }, 0);
    // If a conversation exists, ensure the socket is live.
    if (store.get('wf_chat_token') && (!ws || ws.readyState > WebSocket.OPEN)) connect();
  }

  function close() {
    if (!state.open) return;
    state.open = false;
    panel.classList.remove('wf-chat-panel--open');
    launcher.setAttribute('aria-expanded', 'false');
    launcher.querySelector('.wf-chat-sr').textContent = 'Open support chat';
    lockBodyScroll(false);
    var restore = state.lastFocus && state.lastFocus.focus ? state.lastFocus : launcher;
    try { restore.focus(); } catch (e) { try { launcher.focus(); } catch (e2) {} }
  }

  var scrollLockY = 0;
  var bodyLocked = false;
  function lockBodyScroll(on) {
    var b = document.body;
    if (on) {
      if (bodyLocked) return;
      bodyLocked = true;
      scrollLockY = window.pageYOffset || 0;
      b.style.position = 'fixed';
      b.style.top = -scrollLockY + 'px';
      b.style.width = '100%';
    } else {
      // Only unwind if we actually locked — otherwise close() on desktop would
      // scrollTo(0, staleY) and jump the page.
      if (!bodyLocked) return;
      bodyLocked = false;
      b.style.position = '';
      b.style.top = '';
      b.style.width = '';
      window.scrollTo(0, scrollLockY);
      scrollLockY = 0;
    }
  }

  function focusableIn(container) {
    var sel = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.prototype.slice.call(container.querySelectorAll(sel)).filter(function (n) {
      return n.offsetParent !== null || n === document.activeElement;
    });
  }

  function focusFirst(container) {
    var f = focusableIn(container);
    // Pointer/touch users skip search boxes: on phones, focusing one pops the
    // keyboard over the list they came to browse. Keyboard users still land
    // in the search box first.
    if (!root.classList.contains('wf-chat-kbd')) {
      f = f.filter(function (n) { return n.type !== 'search'; });
    }
    if (f.length) { try { f[0].focus(); } catch (e) {} }
  }

  function onGlobalKeydown(e) {
    if (!state.open) return;
    // Only act while focus is INSIDE our widget. If the user has tabbed/opened
    // into the theme's cart drawer or search modal (which layer ABOVE us), that
    // dialog owns Escape/Tab — hijacking them would close our panel instead of
    // the drawer and yank focus out of it.
    if (!root.contains(document.activeElement)) return;

    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
      return;
    }
    if (e.key === 'Tab') {
      // Wrap focus at the panel's edges. No unconditional force-focus branch:
      // if focus somehow isn't in the panel we leave the key alone rather than
      // snatching it back.
      var f = focusableIn(panel);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        try { last.focus(); } catch (err) {}
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        try { first.focus(); } catch (err) {}
      }
    }
  }
})();
